// src/components/Navbar.js
import axios from "axios";
import {
  Award,
  Bookmark,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Coffee,
  CreditCard,
  Database,
  ExternalLink,
  Facebook,
  FileText,
  Gift,
  Heart,
  Home,
  Image,
  Users as Info,
  Instagram,
  Layers,
  Linkedin,
  LogOut,
  Mail,
  Map,
  Menu,
  MessageCircle,
  Package,
  PenTool,
  Phone,
  Settings,
  ShoppingBag,
  ShoppingCartIcon,
  Star,
  Tag,
  Truck,
  Twitter,
  User,
  UserRoundCog,
  X,
  Youtube
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useConfig } from "../context/ConfigContext";

// Map icon names to components
const iconMap = {
  Home: Home,
  Package: Package,
  Mail: Mail,
  Users: Info,
  Info: Info,
  ExternalLink: ExternalLink,
  ShoppingCart: ShoppingCartIcon,
  User: User,
  Settings: Settings,
  FileText: FileText,
  Phone: Phone,
  MessageCircle: MessageCircle,
  Calendar: Calendar,
  Map: Map,
  Bookmark: Bookmark,
  Award: Award,
  Database: Database,
  Layers: Layers,
  Coffee: Coffee,
  Gift: Gift,
  Tag: Tag,
  Clock: Clock,
  CreditCard: CreditCard,
  PenTool: PenTool,
  Truck: Truck,
  Image: Image,
  Heart: Heart,
  Star: Star,
  ShoppingBag: ShoppingBag,
  Linkedin: Linkedin,
  Twitter: Twitter,
  Facebook: Facebook,
  Instagram: Instagram,
  Youtube: Youtube,
};

// Function to render icon based on name
const renderIcon = (iconName) => {
  if (!iconName) return null;

  const IconComponent = iconMap[iconName];
  if (IconComponent) {
    return <IconComponent className="w-4 h-4 mr-1" />;
  }
  return null;
};

// Generate inline styles from menu styling options
const generateItemStyles = (
  item,
  baseStyles,
  isActive = false,
  hoverState = false
) => {
  const styles = { ...baseStyles };

  // Apply menu's base styles
  if (baseStyles) {
    if (!hoverState && !isActive) {
      if (baseStyles.textColor) styles.color = baseStyles.textColor;
      if (baseStyles.backgroundColor)
        styles.backgroundColor = baseStyles.backgroundColor;
    } else if (hoverState && !isActive) {
      if (baseStyles.hoverTextColor) styles.color = baseStyles.hoverTextColor;
      if (baseStyles.hoverBackgroundColor)
        styles.backgroundColor = baseStyles.hoverBackgroundColor;
    } else if (isActive) {
      if (baseStyles.activeTextColor) styles.color = baseStyles.activeTextColor;
      if (baseStyles.activeBackgroundColor)
        styles.backgroundColor = baseStyles.activeBackgroundColor;
    }

    if (baseStyles.fontFamily) styles.fontFamily = baseStyles.fontFamily;
    if (baseStyles.fontSize) styles.fontSize = baseStyles.fontSize;
    if (baseStyles.fontWeight) styles.fontWeight = baseStyles.fontWeight;
    if (baseStyles.borderRadius) styles.borderRadius = baseStyles.borderRadius;

    // Add border properties
    if (baseStyles.borderWidth) styles.borderWidth = baseStyles.borderWidth;
    if (baseStyles.borderStyle) styles.borderStyle = baseStyles.borderStyle;
    if (baseStyles.borderColor) styles.borderColor = baseStyles.borderColor;
  }

  // Override with item-specific styles if provided
  if (item?.styles) {
    if (!hoverState && !isActive) {
      if (item.styles.textColor) styles.color = item.styles.textColor;
      if (item.styles.backgroundColor)
        styles.backgroundColor = item.styles.backgroundColor;
    } else if (hoverState && !isActive) {
      if (item.styles.hoverTextColor) styles.color = item.styles.hoverTextColor;
      if (item.styles.hoverBackgroundColor)
        styles.backgroundColor = item.styles.hoverBackgroundColor;
    }

    if (item.styles.fontFamily) styles.fontFamily = item.styles.fontFamily;
    if (item.styles.fontSize) styles.fontSize = item.styles.fontSize;
    if (item.styles.fontWeight) styles.fontWeight = item.styles.fontWeight;
    if (item.styles.borderRadius)
      styles.borderRadius = item.styles.borderRadius;

    // Add border properties for item-specific styles
    if (item.styles.borderWidth) styles.borderWidth = item.styles.borderWidth;
    if (item.styles.borderStyle) styles.borderStyle = item.styles.borderStyle;
    if (item.styles.borderColor) styles.borderColor = item.styles.borderColor;
  }

  return styles;
};

const Navbar = ({ menuName = "navbar", fallbackMenuName = "main" }) => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const [expandedMobileItems, setExpandedMobileItems] = useState({});

  //for centered scrolling
  const [showLogo, setShowLogo] = useState(true);
  const [navbarHeight, setNavbarHeight] = useState(null);
  const [navbarOpacity, setNavbarOpacity] = useState(1);

  // Dynamic menu state
  const [menuData, setMenuData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);

  // Hover state for menu items
  const [hoveredItem, setHoveredItem] = useState(null);

  // Custom style states
  const [navbarLogo, setNavbarLogo] = useState(null);
  const [customStyles, setCustomStyles] = useState({});
  const [navbarOptions, setNavbarOptions] = useState({});

  // Check if current path is the admin dashboard
  const isAdminDashboard = location.pathname === "/pitech";

  //for phone & email shown in cenetred
  const { config } = useConfig();
  const contactEmail = config.contactEmail;
  const contactPhone = config.contactPhone;

  // Effect for handling admin dashboard specific styling
  useEffect(() => {
    const fixAdminDashboardPadding = () => {
      // Fix for admin dashboard
      if (isAdminDashboard) {
        document.body.style.paddingTop = "40px"; // Fixed padding for admin dashboard

        const navbar = document.querySelector("nav");
        if (navbar) {

          navbar.style.height = "40px"; // Fixed height for navbar on admin dashboard
          setNavbarHeight("40px");
          if (navbarOptions.layout === "centered") {
            setShowLogo(true);
          }
        }
      }
    };
    fixAdminDashboardPadding();
  });

  useEffect(() => {
    // Set initial navbar positioning immediately
    const navbar = document.querySelector("nav");
    if (navbar) {
      navbar.style.top = "0";
      navbar.style.position = "fixed";
    }
  }, []);

  //function to sort menu items by order
  const sortMenuItemsByOrder = (items) => {
    return [...items].sort((a, b) => a.order - b.order);
  };

  // Fetch dynamic menu items
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(`/api/menus/name/${menuName}`);
        const menu = response.data;

        setMenuData(menu);

        // Create a deep copy of the menu items array and sort by order
        let sortedItems = [];
        if (menu.items && menu.items.length > 0) {
          // Create deep copy to avoid mutation issues
          sortedItems = JSON.parse(JSON.stringify(menu.items));
          // Sort by order property
          sortedItems.sort((a, b) => {
            // If order properties are the same, maintain original order
            if (a.order === b.order) return 0;
            // Otherwise sort by order property
            return a.order - b.order;
          });

          // Also sort children of each item
          sortedItems.forEach((item) => {
            if (item.children && item.children.length > 0) {
              item.children.sort((a, b) => a.order - b.order);
            }
          });
        }

        // Set the sorted items in state
        setMenuItems(sortedItems);

        // Extract styling information
        if (menu.styles) {
          setCustomStyles(menu.styles);
        }

        if (menu.navbarOptions) {
          setNavbarOptions(menu.navbarOptions);
          if (menu.navbarOptions.logo) {
            setNavbarLogo(menu.navbarOptions.logo);
          }
        }

        // Load custom CSS if provided
        if (menu.customCss) {
          // Create a style element for custom CSS
          const styleElement = document.createElement("style");
          styleElement.id = `menu-custom-css-${menu._id}`;
          styleElement.textContent = menu.customCss;
          document.head.appendChild(styleElement);

          // Cleanup function
          return () => {
            const existingStyle = document.getElementById(
              `menu-custom-css-${menu._id}`
            );
            if (existingStyle) {
              document.head.removeChild(existingStyle);
            }
          };
        }
      } catch (error) {
        console.error("Error fetching menu:", error);
      } finally {
        setMenuLoading(false);
      }
    };

    fetchMenu();
  }, [menuName]);

  const toggleMobileItem = (itemId) => {
    setExpandedMobileItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Generate CSS classes based on configuration
  const getNavbarClasses = () => {
    const baseClasses = "fixed w-full z-50 transition-all duration-300";

    // Add background color classes based on scroll state
    const bgClasses = isScrolled
      ? "bg-gray-900 shadow-lg py-2"
      : "bg-gray-800/90 backdrop-blur-sm py-4";

    return `${baseClasses} ${bgClasses} ${customStyles.customClasses || ""}`;
  };

  // Generate container class based on navbarOptions
  const getContainerClasses = () => {
    let containerClass = "w-full px-4 sm:px-6 lg:px-8";

    if (
      navbarOptions.containerMaxWidth &&
      navbarOptions.containerMaxWidth !== "100%"
    ) {
      containerClass += " mx-auto";
    }

    return containerClass;
  };

  function darkenColor(hex, amount = 20) {
    let col = hex.startsWith("#") ? hex.slice(1) : hex;
    if (col.length === 3)
      col = col
        .split("")
        .map((c) => c + c)
        .join("");

    const num = parseInt(col, 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0x00ff) - amount);
    const b = Math.max(0, (num & 0x0000ff) - amount);

    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  // Render menu items dynamically
  const renderMenuItems = (items) => {
    return items.map((item) => {
      // Skip items that should not be shown on this device
      if (
        (item.mobileOnly &&
          window.innerWidth >=
          parseInt(navbarOptions.mobileBreakpoint || "768px")) ||
        (item.desktopOnly &&
          window.innerWidth <
          parseInt(navbarOptions.mobileBreakpoint || "768px"))
      ) {
        return null;
      }

      const isActive = location.pathname === item.path;
      const itemClasses = `px-2 py-2 rounded-md text-sm font-medium ${isActive
        ? "text-blue-400 bg-gray-700"
        : "text-gray-300 hover:text-blue-400 hover:bg-gray-700"
        } transition-all duration-200 ${item.customClasses || ""}`;

      // Generate dynamic styles
      const itemStyles = generateItemStyles(item, customStyles, isActive);
      const hoverStyles = generateItemStyles(
        item,
        customStyles,
        isActive,
        true
      );

      return (
        <React.Fragment key={item._id}>
          {/* Render child items only if they exist */}
          {item.children && item.children.length > 0 ? (
            <div className="group relative inline-block hover:delay-150">
              {/* Parent menu item */}
              <div
                className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === item.path
                  ? "text-blue-400 bg-gray-700"
                  : "text-gray-300 hover:text-blue-400 hover:bg-gray-700"
                  } transition-all duration-200 cursor-pointer flex items-center ${item.customClasses || ""
                  }`}
                style={hoveredItem === item._id ? hoverStyles : itemStyles}
                onMouseEnter={() => setHoveredItem(item._id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {item.icon && renderIcon(item.icon)}
                {item.title}
                <ChevronDown className="w-3 h-3 ml-1" />
              </div>

              {/* Dropdown for children */}
              <div className="absolute left-0 -mt-2 pt-6 hidden group-hover:block bg-gray-800 rounded-md shadow-lg py-1 min-w-max z-50 transition-opacity duration-300 group-hover:opacity-100 opacity-0 group-hover:delay-0">
                {item.children.map((child) => {
                  // Skip children based on display settings
                  if (
                    (child.mobileOnly &&
                      window.innerWidth >=
                      parseInt(navbarOptions.mobileBreakpoint || "768px")) ||
                    (child.desktopOnly &&
                      window.innerWidth <
                      parseInt(navbarOptions.mobileBreakpoint || "768px"))
                  ) {
                    return null;
                  }

                  const isChildActive = location.pathname === child.path;
                  const childStyles = generateItemStyles(
                    child,
                    customStyles,
                    isChildActive
                  );
                  const childHoverStyles = generateItemStyles(
                    child,
                    customStyles,
                    isChildActive,
                    true
                  );

                  return (
                    <React.Fragment key={child._id}>
                      {child.isExternal ? (
                        <a
                          href={child.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400 whitespace-nowrap ${child.customClasses || ""
                            }`}
                          style={
                            hoveredItem === child._id
                              ? childHoverStyles
                              : childStyles
                          }
                          onMouseEnter={() => setHoveredItem(child._id)}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          <span className="flex items-center">
                            {child.icon && renderIcon(child.icon)}
                            {child.title}
                            {!child.icon && (
                              <ExternalLink className="w-3 h-3 ml-1" />
                            )}
                          </span>
                        </a>
                      ) : (
                        <Link
                          to={child.path}
                          onClick={showPageLoading}
                          className={`block px-4 py-2 text-sm ${location.pathname === child.path
                            ? "text-blue-400 bg-gray-700"
                            : "text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                            } whitespace-nowrap ${child.customClasses || ""}`}
                          style={
                            hoveredItem === child._id
                              ? childHoverStyles
                              : childStyles
                          }
                          onMouseEnter={() => setHoveredItem(child._id)}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          <span className="flex items-center">
                            {child.icon && renderIcon(child.icon)}
                            {child.title}
                          </span>
                        </Link>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ) : // Regular menu item with no children
            item.isExternal ? (
              <a
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className={itemClasses}
                style={hoveredItem === item._id ? hoverStyles : itemStyles}
                onMouseEnter={() => setHoveredItem(item._id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="flex items-center">
                  {item.icon && renderIcon(item.icon)}
                  {item.title}
                  {!item.icon && <ExternalLink className="w-3 h-3 ml-1" />}
                </span>
              </a>
            ) : (
              <Link
                to={item.path}
                onClick={showPageLoading}
                className={itemClasses}
                style={hoveredItem === item._id ? hoverStyles : itemStyles}
                onMouseEnter={() => setHoveredItem(item._id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="flex items-center">
                  {item.icon && renderIcon(item.icon)}
                  {item.title}
                </span>
              </Link>
            )}
        </React.Fragment>
      );
    });
  };

  // Function to render mobile menu items recursively
  const renderMobileMenuItems = (items, depth = 0) => {
    return items.map((item) => {
      // Skip items that should not be shown on mobile
      if (item.desktopOnly) {
        return null;
      }

      const isActive = location.pathname === item.path;
      const itemStyles = generateItemStyles(item, customStyles, isActive);

      return (
        <React.Fragment key={item._id}>
          {item.children && item.children.length > 0 ? (
            <div>
              <div
                className={`block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700 ${depth > 0 ? "pl-8" : ""
                  } cursor-pointer ${item.customClasses || ""}`}
                onClick={() => toggleMobileItem(item._id)}
                style={itemStyles}
              >
                <span className="flex items-center justify-between">
                  <span className="flex items-center">
                    {item.icon && renderIcon(item.icon)}
                    {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
                    {item.title}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${expandedMobileItems[item._id] ? "rotate-180" : ""
                      }`}
                  />
                </span>
              </div>

              <div
                className={`ml-2 border-l border-gray-700 overflow-hidden transition-all duration-300 ${expandedMobileItems[item._id] ? "max-h-96" : "max-h-0"
                  }`}
              >
                {renderMobileMenuItems(
                  item.children.filter((child) => !child.desktopOnly),
                  depth + 1
                )}
              </div>
            </div>
          ) : item.isExternal ? (
            <a
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className={`block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700 ${depth > 0 ? "pl-8" : ""
                } ${item.customClasses || ""}`}
              onClick={handleLinkClick}
              style={itemStyles}
            >
              <span className="flex items-center">
                {item.icon && renderIcon(item.icon)}
                {!item.icon && depth === 0 && (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
                {item.title}
              </span>
            </a>
          ) : (
            <Link
              to={item.path}
              onClick={handleLinkClick}
              className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === item.path
                ? "text-blue-400 bg-gray-700"
                : "text-gray-300 hover:text-blue-400 hover:bg-gray-700"
                } ${depth > 0 ? "pl-8" : ""} ${item.customClasses || ""}`}
              style={itemStyles}
            >
              <span className="flex items-center">
                {item.icon && renderIcon(item.icon)}
                {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
                {item.title}
              </span>
            </Link>
          )}
        </React.Fragment>
      );
    });
  };

  // scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
        setNavbarOpacity(0.95); // Set opacity when scrolled
        // In centered layout when scrolled
        if (navbarOptions.layout === "centered") {
          setShowLogo(false);
          setNavbarHeight("80px"); // Set fixed height when scrolled
        }
      } else {
        setIsScrolled(false);
        setNavbarOpacity(1); // Reset to full opacity when at top
        setShowLogo(true);
        // Reset to original height from options
        setNavbarHeight(
          window.innerWidth >
            parseInt(navbarOptions.mobileBreakpoint || "768px")
            ? navbarOptions.heightDesktop || "80px"
            : navbarOptions.heightMobile || "70px"
        );
      }
    };

    // Initialize the height and opacity
    setNavbarHeight(
      window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || "768px")
        ? navbarOptions.heightDesktop || "80px"
        : navbarOptions.heightMobile || "70px"
    );

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    navbarOptions.layout,
    navbarOptions.heightDesktop,
    navbarOptions.heightMobile,
    navbarOptions.mobileBreakpoint,
  ]);

  useEffect(() => {
    // Get navbar height from options or use defaults
    const desktopHeight = navbarOptions.heightDesktop || "80px";
    const mobileHeight = navbarOptions.heightMobile || "70px";
    const mobileBreakpoint = navbarOptions.mobileBreakpoint || "768px";

    // Convert px values to numbers for calculations
    const getPixelValue = (value) => {
      if (typeof value === "string") {
        return parseInt(value) || (value.includes("px") ? parseInt(value) : 80);
      }
      return value || 80;
    };

    // Set the correct height directly on the navbar element
    const navbar = document.querySelector("nav");
    if (navbar) {
      // Remove negative margins and set position instead
      navbar.style.marginTop = "0";
      navbar.style.top = "0";

      if (window.innerWidth > parseInt(mobileBreakpoint)) {
        navbar.style.height = `${getPixelValue(desktopHeight)}px`;
      } else {
        navbar.style.height = `${getPixelValue(mobileHeight)}px`;
      }
    }

    // Add padding to body to match the navbar height
    document.body.style.paddingTop =
      window.innerWidth > parseInt(mobileBreakpoint)
        ? `${getPixelValue(desktopHeight)}px`
        : `${getPixelValue(mobileHeight)}px`;

    // Add media query for responsive padding
    const mediaQuery = window.matchMedia(`(max-width: ${mobileBreakpoint})`);
    const handleMediaChange = (e) => {
      if (e.matches) {
        document.body.style.paddingTop = `${getPixelValue(mobileHeight)}px`;
        if (navbar) navbar.style.height = `${getPixelValue(mobileHeight)}px`;
      } else {
        document.body.style.paddingTop = `${getPixelValue(desktopHeight)}px`;
        if (navbar) navbar.style.height = `${getPixelValue(desktopHeight)}px`;
      }
    };

    // Set initial padding based on screen size
    handleMediaChange(mediaQuery);

    // Add listener for screen size changes
    mediaQuery.addEventListener("change", handleMediaChange);

    // Clean up
    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange);
      document.body.style.paddingTop = "0";
    };
  }, [navbarOptions]);

  const toggleMenu = () => setIsOpen(!isOpen);

  // Function to show loading screen
  const showPageLoading = () => {
    document.body.classList.add("page-loading");

    // Automatically remove loading after a short timeout (for demo)
    setTimeout(() => {
      document.body.classList.remove("page-loading");
    }, 600);
  };

  // Function to close menu when a mobile link is clicked
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const handleLogout = () => {
    showPageLoading();
    logout();
    // Redirect to home or login page after logout
  };

  // Get navbar layout style
  const getNavbarLayout = () => {
    const layout = navbarOptions.layout || "standard";

    switch (layout) {
      case "centered":
        return "flex-col items-center";
      case "split":
        return "justify-between items-center";
      case "dynamic":
        return "justify-between items-center gap-10";
      default: // standard
        return "justify-between items-center";
    }
  };

  if (menuLoading) {
    return (
      <div
        className="fixed w-full h-16 z-50"
        style={{
          backgroundColor: "transparent",
          transition: "background-color 0.3s ease",
        }}
      ></div>
    );
  }
  const getLogoPosition = () => {
    if (navbarOptions.layout !== 'dynamic') return 'left';
    return navbarOptions.logoPosition || 'left';
  };
  // 3. Create a helper function to render contact information
  const renderContactInfo = () => {
    if (!navbarOptions.showContactInDynamic || navbarOptions.layout !== 'dynamic') return null;

    const position = navbarOptions.contactPositionDynamic || 'top-right';
    const style = navbarOptions.contactStyle || 'both';

    const contactElement = (
      <div className={`flex items-center space-x-4 ${getContactPositionClasses(position)}`}>
        {(style === 'phone-only' || style === 'both') && contactPhone && (
          <a
            href={`tel:${contactPhone}`}
            className="flex items-center text-sm transition-colors hover:text-blue-400"
            style={{
              color: customStyles.textColor || '#D1D5DB',
              // Apply custom styles
              ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
              ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
              ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
              ...(customStyles.transition && { transition: customStyles.transition }),
              ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
              ...(customStyles.padding && { padding: customStyles.padding }),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
              if (customStyles.hoverBackgroundColor) {
                e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Phone className="w-4 h-4 mr-1" />
            {style !== 'icons-only' && <span>{contactPhone}</span>}
          </a>
        )}
        {(style === 'email-only' || style === 'both') && contactEmail && (
          <a
            href={`mailto:${contactEmail}`}
            className="flex items-center text-sm transition-colors hover:text-blue-400"
            style={{
              color: customStyles.textColor || '#D1D5DB',
              // Apply custom styles
              ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
              ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
              ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
              ...(customStyles.transition && { transition: customStyles.transition }),
              ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
              ...(customStyles.padding && { padding: customStyles.padding }),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
              if (customStyles.hoverBackgroundColor) {
                e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Mail className="w-4 h-4 mr-1" />
            {style !== 'icons-only' && <span>{contactEmail}</span>}
          </a>
        )}
      </div>
    );

    return contactElement;
  };
  // 4. Helper function for contact position classes
  const getContactPositionClasses = (position) => {
    switch (position) {
      case 'top-left': return 'absolute top-2 left-4';
      case 'top-center': return 'absolute top-2 left-1/2 transform -translate-x-1/2';
      case 'top-right': return 'absolute top-2 right-4';
      case 'inline-left': return 'order-1';
      case 'inline-right': return 'order-3';
      case 'bottom': return 'absolute bottom-2 left-1/2 transform -translate-x-1/2';
      default: return 'absolute top-2 right-4';
    }
  };
  // 5. Create a helper function to render social media links
  const renderSocialMedia = () => {
    if (!navbarOptions.showSocialInDynamic || navbarOptions.layout !== 'dynamic') return null;

    // Get social links from navbar options or menu data
    const socialLinks = navbarOptions.socialLinks || [];
    if (socialLinks.length === 0) return null;

    const position = navbarOptions.socialPosition || 'top-right';
    const iconSize = getSocialIconSize();

    // Social icon mapping
    const socialIconMap = {
      facebook: Facebook,
      instagram: Instagram,
      twitter: Twitter,
      linkedin: Linkedin,
      youtube: Youtube
    };

    return (
      <div className={`flex items-center space-x-2 ${getSocialPositionClasses(position)}`}>
        {socialLinks.map((social, index) => {
          const SocialIcon = socialIconMap[social.platform];
          if (!SocialIcon) return null;

          return (
            <a
              key={index}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-blue-400 transition-colors"
              style={{
                color: customStyles.textColor || '#D1D5DB',
                // Apply custom styles
                ...(customStyles.transition && { transition: customStyles.transition }),
                ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                ...(customStyles.padding && { padding: customStyles.padding }),
                ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                if (customStyles.hoverBackgroundColor) {
                  e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <SocialIcon className={iconSize} />
            </a>
          );
        })}
      </div>
    );
  };


  // 6. Helper functions for social media
  const getSocialIconSize = () => {
    const size = navbarOptions.socialIconSize || 'medium';
    switch (size) {
      case 'small': return 'w-4 h-4';
      case 'large': return 'w-6 h-6';
      default: return 'w-5 h-5'; // medium
    }
  };

  const getSocialPositionClasses = (position) => {
    switch (position) {
      case 'top-left': return 'absolute top-2 left-4';
      case 'top-center': return 'absolute top-2 left-1/2 transform -translate-x-1/2';
      case 'top-right': return 'absolute top-2 right-4';
      case 'inline-left': return 'order-1';
      case 'inline-right': return 'order-3';
      case 'bottom': return 'absolute bottom-2 left-1/2 transform -translate-x-1/2';
      default: return 'absolute top-2 right-4';
    }
  };

  // 7. Create a search bar component
  const renderSearchBar = () => {
    if (!navbarOptions.showSearchBar || navbarOptions.layout !== 'dynamic' || isAdminDashboard) return null;

    const position = navbarOptions.searchPosition || 'center';
    const placeholder = navbarOptions.searchPlaceholder || 'Search...';

    return (
      <div className={`${getSearchPositionClasses(position)}`}>
        <div className="relative">
          <input
            type="search"
            placeholder={placeholder}
            className="bg-gray-700 text-white placeholder-gray-400 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{
              // Apply custom styles to search input
              ...(customStyles.backgroundColor && { backgroundColor: customStyles.backgroundColor }),
              ...(customStyles.textColor && { color: customStyles.textColor }),
              ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
              ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
              ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
              ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
              ...(customStyles.boxShadow && { boxShadow: customStyles.boxShadow }),
              ...(customStyles.transition && { transition: customStyles.transition }),
              ...(customStyles.borderWidth && { borderWidth: customStyles.borderWidth }),
              ...(customStyles.borderStyle && { borderStyle: customStyles.borderStyle }),
              ...(customStyles.borderColor && { borderColor: customStyles.borderColor }),
            }}
          />
          <button
            className="absolute right-2 top-1/2 transform -translate-y-1/2"
            style={{
              // Apply styles to search button
              ...(customStyles.textColor && { color: customStyles.textColor }),
              ...(customStyles.transition && { transition: customStyles.transition }),
            }}
          >
            {/* <Search className="w-4 h-4 text-gray-400" /> */}
          </button>
        </div>
      </div>
    );
  };

  const getSearchPositionClasses = (position) => {
    switch (position) {
      case 'left': return 'order-1';
      case 'right': return 'order-3';
      case 'expand': return 'flex-grow mx-4';
      default: return 'order-2'; // center
    }
  };


  // 9. Helper functions for logo and menu positioning
  const getLogoOrderClass = () => {
    const position = navbarOptions.logoPosition || 'left';
    switch (position) {
      case 'center': return 'order-2';
      case 'right': return 'order-3';
      default: return 'order-1'; // left
    }
  };

  const getMenuOrderClass = () => {
    const position = navbarOptions.menuPosition || 'center';
    const logoPosition = navbarOptions.logoPosition || 'left';

    // Adjust menu order based on logo position
    if (logoPosition === 'center') {
      switch (position) {
        case 'left': return 'order-1';
        case 'right': return 'order-3';
        default: return 'order-2';
      }
    }

    switch (position) {
      case 'left': return 'order-1';
      case 'right': return 'order-3';
      default: return 'order-2'; // center
    }
  };

  // 10. Logo scroll behavior functions
  const getLogoScrollClasses = () => {
    if (!isScrolled) return '';

    const behavior = navbarOptions.logoScrollBehavior || 'shrink';
    switch (behavior) {
      case 'hide': return 'scale-0 opacity-0';
      case 'fade': return 'opacity-50';
      case 'shrink': return 'scale-90';
      default: return '';
    }
  };

  const getLogoScrollStyles = () => {
    if (!isScrolled) return {
      maxHeight: navbarOptions.logoHeight || '60px',
      width: navbarOptions.logoWidth || 'auto'
    };

    const behavior = navbarOptions.logoScrollBehavior || 'shrink';
    const baseHeight = parseInt(navbarOptions.logoHeight || '60px');

    switch (behavior) {
      case 'shrink':
        return {
          maxHeight: `${baseHeight * 0.8}px`,
          width: navbarOptions.logoWidth || 'auto'
        };
      case 'hide':
        return {
          maxHeight: '0px',
          width: '0px'
        };
      default:
        return {
          maxHeight: navbarOptions.logoHeight || '60px',
          width: navbarOptions.logoWidth || 'auto'
        };
    }
  };

  // 11. Menu scroll behavior function
  const getMenuScrollClasses = () => {
    if (!isScrolled) return '';

    const behavior = navbarOptions.menuScrollBehavior || 'shrink';
    switch (behavior) {
      case 'shrink': return 'scale-95 opacity-90';
      case 'compress': return 'space-x-0.5';
      case 'minimal': return 'scale-90 opacity-75';
      default: return '';
    }
  };
  return (
    <nav
      className={getNavbarClasses()}
      style={{
        top: "0",
        position: "fixed",
        height: isAdminDashboard
          ? "80px"
          : navbarHeight ||
          (window.innerWidth >
            parseInt(navbarOptions.mobileBreakpoint || "768px")
            ? navbarOptions.heightDesktop || "80px"
            : navbarOptions.heightMobile || "70px"),

        // height: navbarHeight || (window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || '768px')
        //   ? navbarOptions.heightDesktop || '80px'
        //   : navbarOptions.heightMobile || '70px'),
        // opacity: navbarOpacity,
        opacity: isAdminDashboard ? 1 : navbarOpacity,
        transition: "height 0.3s ease, opacity 0.3s ease",
        ...(customStyles.backgroundColor && {
          backgroundColor: customStyles.backgroundColor,
        }),
        ...(navbarOptions.containerMaxWidth && {
          maxWidth: navbarOptions.containerMaxWidth,
        }),
        ...(navbarOptions.layout === 'dynamic' && {
          ...(customStyles.padding && { padding: customStyles.padding }),
          ...(customStyles.margin && { margin: customStyles.margin }),
          ...(customStyles.boxShadow && { boxShadow: customStyles.boxShadow }),
          ...(navbarOptions.enableBlur && {
            backdropFilter: `blur(${navbarOptions.blurAmount || '8px'})`
          }),
          ...(navbarOptions.enableGradient && navbarOptions.gradientValue && {
            background: navbarOptions.gradientValue
          }),
        }),
      }}
    >

      <div
        className={getContainerClasses()}
        style={{
          height: "100%", // Make container take full height
          ...(navbarOptions.containerMaxWidth
            ? { maxWidth: navbarOptions.containerMaxWidth }
            : {}),
        }}
      >
        {/* Mobile layout with hamburger-logo-cart */}
        <div className="flex items-center justify-between w-full md:hidden h-full">
          <div className="flex-1 flex justify-start">
            {!isAdminDashboard && (
              <button
                onClick={toggleMenu}
                className="p-1 rounded-md focus:outline-none hover:bg-gray-700 transition-all duration-200"
                style={{
                  boxShadow: "none",
                  border: "none",
                  background: "transparent",
                  transform: "none",
                  padding: 0,
                  overflow: "visible",
                }}
              >
                {isOpen ? (
                  <X
                    className="w-6 h-6 text-gray-300"
                    style={{ color: customStyles.activeBackgroundColor }}
                  />
                ) : (
                  <Menu
                    className="w-6 h-6 text-gray-300"
                    style={{ color: customStyles.activeBackgroundColor }}
                  />
                )}
              </button>
            )}
          </div>

          {/* Logo - center on mobile */}
          <div className="flex-1 flex items-center justify-center">
            <Link
              to="/"
              className="flex items-center h-full"
              onClick={showPageLoading}
            >
              {navbarLogo ? (
                <img
                  className="transition-all duration-300 hover:scale-105"
                  src={navbarLogo}
                  alt="Logo"
                  style={{
                    maxHeight: navbarOptions.logoHeight || "60px",
                    width: navbarOptions.logoWidth || "auto",
                  }}
                />
              ) : (
                // Fallback logo or text if no logo is set
                <div className="text-xl font-bold text-white">LOGO</div>
              )}
            </Link>
          </div>

          {/* Cart - right on mobile */}
          <div className="flex-1 flex justify-end">
            <Link
              to="/cart"
              className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "/cart";
              }}
            >
              <ShoppingCartIcon
                style={{ color: customStyles.activeBackgroundColor }}
                className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
              />
              {cart.length > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                  style={{
                    backgroundColor:
                      customStyles.activeBackgroundColor || "#2563EB",
                  }}
                >
                  {cart.length}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Desktop Layout - based on navbarOptions.layout */}
        <div className="hidden md:flex h-full w-full items-center">
          {navbarOptions.layout === "centered" ? (
            /* Centered Layout (Logo top, menu center) */
            <div className="flex flex-col w-full h-full justify-center items-center">
              {/* {navbarOptions.layout === 'centered' && navbarOptions.showContactInfo && (
              <div className="absolute top-3 left-1 right-1 w-full flex justify-between items-center text-xs px-4" 
                  style={{ height: '24px' }}>
                <div className="flex items-center">
                  {contactPhone && (
                    <a href={`tel:${contactPhone}`} 
                    style={{color: customStyles.textColor || '#D1D5DB'}}
                    className="flex items-center text-md transition-colors">
                      <Phone className="w-4 h-4 mr-1" /> 
                      <span style={{fontSize: 16+'px'}}>{contactPhone}</span>
                    </a>
                  )}
                </div>
                <div className="flex items-center">
                  {contactEmail && (
                    <a href={`mailto:${contactEmail}`} 
                    style={{color: customStyles.textColor || '#D1D5DB'}}
                    className="flex items-center text-md transition-colors">
                      <Mail className="w-4 h-4 mr-1" /> 
                      <span style={{fontSize: 16+'px'}}>{contactEmail}</span>
                    </a>
                  )}
                </div>
              </div>
            )} */}
              {navbarOptions.layout === "centered" &&
                navbarOptions.showContactInfo && (
                  <div
                    className="absolute left-1 right-1 w-full flex justify-between items-center text-xs px-4"
                    style={{
                      height: "24px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <div className="flex items-center">
                      {contactPhone && (
                        <a
                          href={`tel:${contactPhone}`}
                          style={{ color: customStyles.textColor || "#D1D5DB" }}
                          className="flex items-center text-md transition-colors"
                        >
                          <Phone className="w-4 h-4 mr-1" />
                          <span style={{ fontSize: 16 + "px" }}>
                            {contactPhone}
                          </span>
                        </a>
                      )}
                    </div>
                    <div className="flex items-center">
                      {contactEmail && (
                        <a
                          href={`mailto:${contactEmail}`}
                          style={{ color: customStyles.textColor || "#D1D5DB" }}
                          className="flex items-center text-md transition-colors"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          <span style={{ fontSize: 16 + "px" }}>
                            {contactEmail}
                          </span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

              {/* Logo on top - only shown when not scrolled */}
              {showLogo && (
                <div
                  className="flex justify-center mb-2 transition-all duration-300"
                  style={{
                    display: isAdminDashboard ? "none" : "block",
                  }}
                >
                  <Link
                    to="/"
                    className="flex items-center"
                    onClick={showPageLoading}
                  >
                    {navbarLogo ? (
                      <img
                        className="transition-all duration-300 hover:scale-105"
                        src={navbarLogo}
                        alt="Logo"
                        style={{
                          maxHeight: navbarOptions.logoHeight || "60px",
                          width: navbarOptions.logoWidth || "auto",
                        }}
                      />
                    ) : (
                      <div className="text-xl font-bold text-white">LOGO</div>
                    )}
                  </Link>
                </div>
              )}

              {/* Menu items centered */}
              <div
                className="flex justify-center items-center space-x-2"
                style={{
                  position: "relative",
                  zIndex: isAdminDashboard ? 20 : 5, // Higher z-index in admin dashboard
                }}
              >
                {!menuLoading && renderMenuItems(menuItems)}

                {/* <Link to="/blog" 
                onClick={showPageLoading}
                className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
                style={location.pathname === '/blog' ? 
                  generateItemStyles(null, customStyles, true) : 
                  generateItemStyles(null, customStyles)}
                onMouseEnter={() => setHoveredItem('blog')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
              </Link> */}

                {navbarOptions.layout === "centered" &&
                  navbarOptions.showContactInfo &&
                  navbarOptions.contactPosition === "inline" && (
                    <div
                      className="flex items-center space-x-2"
                      style={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >

                      {contactPhone && (
                        <a
                          href={`tel:${contactPhone}`}
                          className="px-3 py-2 rounded-md text-md font-medium transition-all duration-200"
                          style={
                            hoveredItem === "phone"
                              ? generateItemStyles(
                                null,
                                customStyles,
                                false,
                                true
                              )
                              : generateItemStyles(null, customStyles)
                          }
                          onMouseEnter={() => setHoveredItem("phone")}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          <span
                            style={{ fontSize: 16 + "px" }}
                            className="flex items-center"
                          >
                            <Phone className="w-6 h-6 mr-1" /> {contactPhone}
                          </span>
                        </a>
                      )}
                      {contactEmail && (
                        <a
                          href={`mailto:${contactEmail}`}
                          className="px-3 py-2 rounded-md text-md font-medium transition-all duration-200"
                          style={
                            hoveredItem === "email"
                              ? generateItemStyles(
                                null,
                                customStyles,
                                false,
                                true
                              )
                              : generateItemStyles(null, customStyles)
                          }
                          onMouseEnter={() => setHoveredItem("email")}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          <span
                            style={{ fontSize: 16 + "px" }}
                            className="flex items-center"
                          >
                            <Mail className="w-6 h-6 mr-1" /> {contactEmail}
                          </span>
                        </a>
                      )}
                    </div>
                  )}

                {/* User and cart buttons */}
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    {user ? (
                      <div className="flex items-center group">
                        <button
                          className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
                          style={{
                            transform: "none",
                            boxShadow: "none",
                            border: "none",
                            color: customStyles.textColor || "#D1D5DB",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              customStyles.hoverBackgroundColor || "#374151";
                            e.currentTarget.style.color =
                              customStyles.hoverTextColor || "#93C5FD";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color =
                              customStyles.textColor || "#D1D5DB";
                          }}
                        >
                          <User className="w-5 h-5" />
                          <span className="text-sm font-medium">
                            {user.firstName || "Account"}
                          </span>
                        </button>

                        {/* Dropdown menu */}
                        <div
                          className="absolute left-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                          style={{
                            backgroundColor:
                              customStyles.backgroundColor || "#1F2937",
                            borderRadius:
                              // customStyles.borderRadius ||
                              "0.375rem",
                          }}
                        >
                          {user.isAdmin && (
                            <Link
                              to="/pitech"
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                              style={{
                                color: customStyles.textColor || "#D1D5DB",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  customStyles.hoverBackgroundColor ||
                                  "#374151";
                                e.currentTarget.style.color =
                                  customStyles.hoverTextColor || "#93C5FD";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.color =
                                  customStyles.textColor || "#D1D5DB";
                              }}
                            >
                              Admin Dashboard
                            </Link>
                          )}
                          <Link
                            to="/profile"
                            onClick={showPageLoading}
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                            style={{
                              color: customStyles.textColor || "#D1D5DB",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                customStyles.hoverBackgroundColor || "#374151";
                              e.currentTarget.style.color =
                                customStyles.hoverTextColor || "#93C5FD";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.color =
                                customStyles.textColor || "#D1D5DB";
                            }}
                          >
                            My Profile
                          </Link>
                          {!user.isAdmin && (
                            <Link
                              to="/orders"
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                              style={{
                                color: customStyles.textColor || "#D1D5DB",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  customStyles.hoverBackgroundColor ||
                                  "#374151";
                                e.currentTarget.style.color =
                                  customStyles.hoverTextColor || "#93C5FD";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.color =
                                  customStyles.textColor || "#D1D5DB";
                              }}
                            >
                              Orders
                            </Link>
                          )}
                          <button
                            onClick={handleLogout}
                            style={{
                              color: customStyles.textColor || "#D1D5DB",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                customStyles.hoverBackgroundColor || "#374151";
                              e.currentTarget.style.color =
                                customStyles.hoverTextColor || "#93C5FD";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.color =
                                customStyles.textColor || "#D1D5DB";
                            }}
                            className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                          >
                            <span className="flex items-center">
                              <LogOut className="w-4 h-4 mr-1" /> Logout
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Link
                          to="/login"
                          style={
                            hoveredItem === "login"
                              ? generateItemStyles(
                                null,
                                customStyles,
                                false,
                                true
                              )
                              : generateItemStyles(null, customStyles)
                          }
                          onMouseEnter={() => setHoveredItem("login")}
                          onMouseLeave={() => setHoveredItem(null)}
                          className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
                        >
                          Login
                        </Link>
                        {/* <Link to="/register" 
                       style={hoveredItem === 'logout' ? 
                        generateItemStyles(null, customStyles, false, true) : 
                        generateItemStyles(null, customStyles)}
                        onMouseEnter={() => setHoveredItem('logout')}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
                        Register
                      </Link> */}
                      </div>
                    )}
                  </div>

                  <Link
                    to="/cart"
                    className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = "/cart";
                    }}
                  >
                    <ShoppingCartIcon
                      style={{ color: customStyles.activeBackgroundColor }}
                      className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
                    />
                    {cart.length > 0 && (
                      <span
                        className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                        style={{
                          backgroundColor:
                            customStyles.activeBackgroundColor || "#2563EB",
                        }}
                      >
                        {cart.length}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            </div>
          ) : navbarOptions.layout === "split" ? (
            /* Split Layout (Logo center, menu on sides) */
            <div className="flex justify-between items-center w-full h-full">
              {/* Left menu items */}
              <div className="flex items-center space-x-2">
                {!menuLoading &&
                  menuItems
                    .slice(0, Math.ceil(menuItems.length / 2))
                    .map((item, idx) => (
                      <React.Fragment key={item._id || idx}>
                        {renderMenuItems([item])}
                      </React.Fragment>
                    ))}
              </div>

              {/* Center logo */}
              <div className="flex justify-center">
                <Link
                  to="/"
                  className="flex items-center"
                  onClick={showPageLoading}
                >
                  {navbarLogo ? (
                    <img
                      className="transition-all duration-300 hover:scale-105"
                      src={navbarLogo}
                      alt="Logo"
                      style={{
                        maxHeight: navbarOptions.logoHeight || "60px",
                        width: navbarOptions.logoWidth || "auto",
                      }}
                    />
                  ) : (
                    <div className="text-xl font-bold text-white">LOGO</div>
                  )}
                </Link>
              </div>

              {/* Right menu items + user/cart */}
              <div className="flex items-center space-x-2">
                {!menuLoading &&
                  menuItems
                    .slice(Math.ceil(menuItems.length / 2))
                    .map((item, idx) => (
                      <React.Fragment key={item._id || idx}>
                        {renderMenuItems([item])}
                      </React.Fragment>
                    ))}

                {/* <Link to="/blog" 
                onClick={showPageLoading}
                className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
                style={location.pathname === '/blog' ? 
                  generateItemStyles(null, customStyles, true) : 
                  generateItemStyles(null, customStyles)}
                onMouseEnter={() => setHoveredItem('blog')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
              </Link> */}

                {/* User and Cart */}
                <div className="flex items-center space-x-2">
                  {user ? (
                    <div className="flex items-center group relative">
                      <button
                        className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
                        style={{
                          transform: "none",
                          boxShadow: "none",
                          border: "none",
                          color: customStyles.textColor || "#D1D5DB",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            customStyles.hoverBackgroundColor || "#374151";
                          e.currentTarget.style.color =
                            customStyles.hoverTextColor || "#93C5FD";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color =
                            customStyles.textColor || "#D1D5DB";
                        }}
                      >
                        <User className="w-5 h-5" />
                        <span className="text-sm font-medium">
                          {user.firstName || "Account"}
                        </span>
                      </button>

                      {/* Dropdown menu */}
                      {/* <div className="absolute right-0 mt-2 top-full w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                      style={{
                        backgroundColor: customStyles.backgroundColor || '#1F2937',
                        borderRadius: customStyles.borderRadius || '0.375rem'
                      }}
                    > */}
                      <div
                        className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                        style={{
                          backgroundColor:
                            customStyles.backgroundColor || "#1F2937",
                          borderRadius:
                            // customStyles.borderRadius ||
                            "0.375rem",
                        }}
                      >
                        {user.isAdmin && (
                          <Link
                            to="/pitech"
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                            style={{
                              color: customStyles.textColor || "#D1D5DB",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                customStyles.hoverBackgroundColor || "#374151";
                              e.currentTarget.style.color =
                                customStyles.hoverTextColor || "#93C5FD";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.color =
                                customStyles.textColor || "#D1D5DB";
                            }}
                          >
                            Admin Dashboard
                          </Link>
                        )}
                        <Link
                          to="/profile"
                          onClick={showPageLoading}
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                          style={{ color: customStyles.textColor || "#D1D5DB" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              customStyles.hoverBackgroundColor || "#374151";
                            e.currentTarget.style.color =
                              customStyles.hoverTextColor || "#93C5FD";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color =
                              customStyles.textColor || "#D1D5DB";
                          }}
                        >
                          My Profile
                        </Link>
                        {!user.isAdmin && (
                          <Link
                            to="/orders"
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                            style={{
                              color: customStyles.textColor || "#D1D5DB",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                customStyles.hoverBackgroundColor || "#374151";
                              e.currentTarget.style.color =
                                customStyles.hoverTextColor || "#93C5FD";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.color =
                                customStyles.textColor || "#D1D5DB";
                            }}
                          >
                            Orders
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          style={{ color: customStyles.textColor || "#D1D5DB" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              customStyles.hoverBackgroundColor || "#374151";
                            e.currentTarget.style.color =
                              customStyles.hoverTextColor || "#93C5FD";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color =
                              customStyles.textColor || "#D1D5DB";
                          }}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                        >
                          <span className="flex items-center">
                            <LogOut className="w-4 h-4 mr-1" /> Logout
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <Link
                        to="/login"
                        style={
                          hoveredItem === "login"
                            ? generateItemStyles(
                              null,
                              customStyles,
                              false,
                              true
                            )
                            : generateItemStyles(null, customStyles)
                        }
                        onMouseEnter={() => setHoveredItem("login")}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
                      >
                        Login
                      </Link>
                      {/* <Link to="/register" 
                        style={hoveredItem === 'logout' ? 
                        generateItemStyles(null, customStyles, false, true) : 
                        generateItemStyles(null, customStyles)}
                        onMouseEnter={() => setHoveredItem('logout')}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
                        Register
                    </Link> */}
                    </div>
                  )}

                  <Link
                    to="/cart"
                    className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = "/cart";
                    }}
                  >
                    <ShoppingCartIcon
                      style={{ color: customStyles.activeBackgroundColor }}
                      className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
                    />
                    {cart.length > 0 && (
                      <span
                        className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                        style={{
                          backgroundColor:
                            customStyles.activeBackgroundColor || "#2563EB",
                        }}
                      >
                        {cart.length}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            </div>
          )// In the dynamic layout section, replace the existing dynamic layout with this:

            : navbarOptions.layout === "dynamic" ? (
              /* Dynamic Layout (Responsive layout that adapts) */
              <div
                className="flex justify-between items-center w-full h-full relative"
                style={{
                  // Apply all dynamic layout styles here
                  ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
                  ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                  ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                  ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                  ...(customStyles.padding && { padding: customStyles.padding }),
                  ...(customStyles.margin && { margin: customStyles.margin }),
                  ...(customStyles.boxShadow && { boxShadow: customStyles.boxShadow }),
                  ...(customStyles.transition && { transition: customStyles.transition }),
                  // Apply animation settings
                  ...(navbarOptions.animationDuration && {
                    transitionDuration: navbarOptions.animationDuration
                  }),
                  ...(navbarOptions.animationEasing && {
                    transitionTimingFunction: navbarOptions.animationEasing
                  }),
                }}
              >
                {/* Contact Information - Top positioning */}
                {renderContactInfo()}
                {/* Social Media - Top positioning */}
                {renderSocialMedia()}

                {/* Main navbar content */}
                <div className="flex justify-between items-center w-full h-full">
                  {/* Logo - responsive positioning */}
                  <div
                    className={`flex-shrink-0 ${getLogoOrderClass()} transition-all duration-300 ${getLogoScrollClasses()}`}
                    style={{
                      // Apply logo-specific styles
                      ...(customStyles.transition && { transition: customStyles.transition }),
                      ...(navbarOptions.animationDuration && {
                        transitionDuration: navbarOptions.animationDuration
                      }),
                      ...(navbarOptions.animationEasing && {
                        transitionTimingFunction: navbarOptions.animationEasing
                      }),
                    }}
                  >
                    <Link
                      to="/"
                      className="flex items-center"
                      onClick={showPageLoading}
                    >
                      {navbarLogo ? (
                        <img
                          className="transition-all duration-300 hover:scale-105"
                          src={navbarLogo}
                          alt="Logo"
                          style={{
                            ...getLogoScrollStyles(),
                            // Apply additional logo styles
                            ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                            ...(customStyles.boxShadow && { boxShadow: customStyles.boxShadow }),
                            ...(customStyles.transition && { transition: customStyles.transition }),
                          }}
                        />
                      ) : (
                        <div
                          className={`text-xl font-bold text-white transition-all duration-300 ${isScrolled ? 'text-lg' : ''}`}
                          style={{
                            // Apply text styles to logo text
                            ...(customStyles.textColor && { color: customStyles.textColor }),
                            ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
                            ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                            ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                            ...(customStyles.transition && { transition: customStyles.transition }),
                          }}
                        >
                          LOGO
                        </div>
                      )}
                    </Link>
                  </div>

                  {/* Search Bar */}
                  <div
                    className={`${getSearchPositionClasses(navbarOptions.searchPosition || 'center')}`}
                    style={{
                      // Apply styles to search container
                      ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                      ...(customStyles.transition && { transition: customStyles.transition }),
                    }}
                  >
                    {renderSearchBar()}
                  </div>

                  {/* Menu - center on desktop */}
                  <div
                    className={`flex-grow flex justify-center items-center space-x-1 ${getMenuOrderClass()} ${getMenuScrollClasses()}`}
                    style={{
                      // Apply menu container styles
                      ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
                      ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                      ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                      ...(customStyles.transition && { transition: customStyles.transition }),
                      ...(navbarOptions.animationDuration && {
                        transitionDuration: navbarOptions.animationDuration
                      }),
                      ...(navbarOptions.animationEasing && {
                        transitionTimingFunction: navbarOptions.animationEasing
                      }),
                    }}
                  >
                    {!menuLoading && renderMenuItems(menuItems)}
                  </div>

                  {/* Actions - always on the right */}
                  <div
                    className={`flex items-center space-x-2 transition-all duration-300 ${isScrolled ? 'scale-95' : ''}`}
                    style={{
                      // Apply action buttons container styles
                      ...(customStyles.transition && { transition: customStyles.transition }),
                      ...(navbarOptions.animationDuration && {
                        transitionDuration: navbarOptions.animationDuration
                      }),
                      ...(navbarOptions.animationEasing && {
                        transitionTimingFunction: navbarOptions.animationEasing
                      }),
                    }}
                  >
                    <div className="relative">
                      {user ? (
                        <div className="flex items-center group">
                          <button
                            className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none transition-all duration-200"
                            style={{
                              transform: 'none',
                              boxShadow: 'none',
                              border: 'none',
                              color: customStyles.textColor || '#D1D5DB',
                              // Apply additional button styles
                              ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
                              ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                              ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                              ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                              ...(customStyles.padding && { padding: customStyles.padding }),
                              ...(customStyles.transition && { transition: customStyles.transition }),
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
                              e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                            }}
                          >
                            <User className="w-5 h-5" />
                            <span className="text-sm font-medium hidden lg:inline">
                              {user.firstName || 'Account'}
                            </span>
                          </button>

                          {/* Dropdown menu */}
                          <div
                            className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                            style={{
                              backgroundColor: customStyles.backgroundColor || '#1F2937',
                              borderRadius: customStyles.borderRadius || '0.375rem',
                              // Apply dropdown styles
                              ...(customStyles.boxShadow && { boxShadow: customStyles.boxShadow }),
                              ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
                              ...(customStyles.transition && { transition: customStyles.transition }),
                            }}
                          >
                            {user.isAdmin && (
                              <Link
                                to="/pitech"
                                className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                                style={{
                                  color: customStyles.textColor || '#D1D5DB',
                                  ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                                  ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                                  ...(customStyles.transition && { transition: customStyles.transition }),
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
                                  e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                                }}
                              >
                                Admin Dashboard
                              </Link>
                            )}
                            <Link
                              to="/profile"
                              onClick={showPageLoading}
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                              style={{
                                color: customStyles.textColor || '#D1D5DB',
                                ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                                ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                                ...(customStyles.transition && { transition: customStyles.transition }),
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
                                e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                              }}
                            >
                              My Profile
                            </Link>
                            {!user.isAdmin && (
                              <Link
                                to="/orders"
                                className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                                style={{
                                  color: customStyles.textColor || '#D1D5DB',
                                  ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                                  ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                                  ...(customStyles.transition && { transition: customStyles.transition }),
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
                                  e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                                }}
                              >
                                Orders
                              </Link>
                            )}
                            <button
                              onClick={handleLogout}
                              style={{
                                color: customStyles.textColor || '#D1D5DB',
                                ...(customStyles.fontSize && { fontSize: customStyles.fontSize }),
                                ...(customStyles.fontWeight && { fontWeight: customStyles.fontWeight }),
                                ...(customStyles.transition && { transition: customStyles.transition }),
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
                                e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                              }}
                              className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                            >
                              <span className="flex items-center">
                                <LogOut className="w-4 h-4 mr-1" /> Logout
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Link
                            to="/login"
                            style={{
                              ...(hoveredItem === 'login'
                                ? generateItemStyles(null, customStyles, false, true)
                                : generateItemStyles(null, customStyles)),
                              // Apply additional button styles
                              ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                              ...(customStyles.transition && { transition: customStyles.transition }),
                            }}
                            onMouseEnter={() => setHoveredItem('login')}
                            onMouseLeave={() => setHoveredItem(null)}
                            className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
                          >
                            <span className="hidden sm:inline">Login</span>
                            <User className="w-4 h-4 sm:hidden" />
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Cart */}
                    <Link
                      to="/cart"
                      className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
                      style={{
                        // Apply cart button styles
                        ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                        ...(customStyles.transition && { transition: customStyles.transition }),
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/cart';
                      }}
                    >
                      <ShoppingCartIcon
                        style={{
                          color: customStyles.activeBackgroundColor || customStyles.textColor || '#D1D5DB',
                        }}
                        className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
                      />
                      {cart.length > 0 && (
                        <span
                          className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                          style={{
                            backgroundColor: customStyles.activeBackgroundColor || '#2563EB',
                            ...(customStyles.fontFamily && { fontFamily: customStyles.fontFamily }),
                            ...(customStyles.borderRadius && { borderRadius: customStyles.borderRadius }),
                          }}
                        >
                          {cart.length}
                        </span>
                      )}
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              /* Standard Layout (Logo left, menu center, actions right) */
              <div className="flex justify-between items-center w-full h-full">
                {/* Logo left */}
                <div className="flex-shrink-0">
                  <Link
                    to="/"
                    className="flex items-center"
                    onClick={showPageLoading}
                  >
                    {navbarLogo ? (
                      <img
                        className="transition-all duration-300 hover:scale-105"
                        src={navbarLogo}
                        alt="Logo"
                        style={{
                          maxHeight: navbarOptions.logoHeight || "60px",
                          width: navbarOptions.logoWidth || "auto",
                        }}
                      />
                    ) : (
                      <div className="text-xl font-bold text-white">LOGO</div>
                    )}
                  </Link>
                </div>

                {/* Menu center */}
                <div className="flex-grow flex justify-center items-center space-x-0">
                  {!menuLoading && renderMenuItems(menuItems)}
                  {/* <Link to="/blog" 
                onClick={showPageLoading}
                className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
                style={location.pathname === '/blog' ? 
                  generateItemStyles(null, customStyles, true) : 
                  generateItemStyles(null, customStyles)}
                onMouseEnter={() => setHoveredItem('blog')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
              </Link> */}
                </div>

                {/* Actions right */}
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    {user ? (
                      <div className="flex items-center group">
                        <button
                          className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
                          style={{
                            transform: "none",
                            boxShadow: "none",
                            border: "none",
                            color: customStyles.textColor || "#D1D5DB",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              customStyles.hoverBackgroundColor || "#374151";
                            e.currentTarget.style.color =
                              customStyles.hoverTextColor || "#93C5FD";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                            e.currentTarget.style.color =
                              customStyles.textColor || "#D1D5DB";
                          }}
                        >
                          <User className="w-5 h-5" />
                          <span className="text-sm font-medium">
                            {user.firstName || "Account"}
                          </span>
                        </button>

                        {/* Dropdown menu - FIXED positioning */}
                        <div
                          className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                          style={{
                            backgroundColor:
                              customStyles.backgroundColor || "#1F2937",
                            borderRadius:
                              //  customStyles.borderRadius ||
                              "0.375rem",
                          }}
                        >
                          {user.isAdmin && (
                            <Link
                              to="/pitech"
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                              style={{
                                color: customStyles.textColor || "#D1D5DB",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  customStyles.hoverBackgroundColor || "#374151";
                                e.currentTarget.style.color =
                                  customStyles.hoverTextColor || "#93C5FD";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.color =
                                  customStyles.textColor || "#D1D5DB";
                              }}
                            >
                              Admin Dashboard
                            </Link>
                          )}
                          <Link
                            to="/profile"
                            onClick={showPageLoading}
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                            style={{ color: customStyles.textColor || "#D1D5DB" }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                customStyles.hoverBackgroundColor || "#374151";
                              e.currentTarget.style.color =
                                customStyles.hoverTextColor || "#93C5FD";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.color =
                                customStyles.textColor || "#D1D5DB";
                            }}
                          >
                            My Profile
                          </Link>
                          {!user.isAdmin && (
                            <Link
                              to="/orders"
                              className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                              style={{
                                color: customStyles.textColor || "#D1D5DB",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  customStyles.hoverBackgroundColor || "#374151";
                                e.currentTarget.style.color =
                                  customStyles.hoverTextColor || "#93C5FD";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.color =
                                  customStyles.textColor || "#D1D5DB";
                              }}
                            >
                              Orders
                            </Link>
                          )}
                          <button
                            onClick={handleLogout}
                            style={{ color: customStyles.textColor || "#D1D5DB" }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                customStyles.hoverBackgroundColor || "#374151";
                              e.currentTarget.style.color =
                                customStyles.hoverTextColor || "#93C5FD";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.color =
                                customStyles.textColor || "#D1D5DB";
                            }}
                            className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                          >
                            <span className="flex items-center">
                              <LogOut className="w-4 h-4 mr-1" /> Logout
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Link
                          to="/login"
                          style={
                            hoveredItem === "login"
                              ? generateItemStyles(
                                null,
                                customStyles,
                                false,
                                true
                              )
                              : generateItemStyles(null, customStyles)
                          }
                          onMouseEnter={() => setHoveredItem("login")}
                          onMouseLeave={() => setHoveredItem(null)}
                          className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
                        >
                          Login
                        </Link>
                        {/* <Link to="/register" 
                        style={hoveredItem === 'logout' ? 
                        generateItemStyles(null, customStyles, false, true) : 
                        generateItemStyles(null, customStyles)}
                        onMouseEnter={() => setHoveredItem('logout')}
                        onMouseLeave={() => setHoveredItem(null)}
                        className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
                        Register
                    </Link> */}
                      </div>
                    )}
                  </div>

                  {/* Cart */}
                  <Link
                    to="/cart"
                    className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = "/cart";
                    }}
                  >
                    <ShoppingCartIcon
                      style={{ color: customStyles.activeBackgroundColor }}
                      className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
                    />
                    {cart.length > 0 && (
                      <span
                        className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                        style={{
                          backgroundColor:
                            customStyles.activeBackgroundColor || "#2563EB",
                        }}
                      >
                        {cart.length}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Mobile menu - hidden on admin dashboard */}
      {!isAdminDashboard && (
        <div
          className={`md:hidden bg-gray-800 shadow-lg overflow-hidden transition-all duration-300 ${isOpen ? "max-h-98 opacity-100" : "max-h-0 opacity-0"
            }`}
          style={{
            backgroundColor: darkenColor(
              customStyles.backgroundColor || "#1F2937"
            ),
            boxShadow: isOpen
              ? "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)"
              : "none",
          }}
        >
          <div className="px-4 pt-2 pb-3 space-y-1">
            {/* Dynamic mobile menu items */}
            {!menuLoading && renderMobileMenuItems(menuItems)}

            {/* <Link
            onClick={handleLinkClick}
            to="/blog"
            style={{color: customStyles.textColor || '#D1D5DB'}}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
              e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
            }}
            className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'}`}
          >
            <span className="flex items-center"><Blog className="w-4 h-4 mr-2" /> Blog</span>
          </Link> */}

            {/* User options */}
            {user ? (
              <>
                <Link
                  onClick={handleLinkClick}
                  to="/profile"
                  style={{
                    color: customStyles.textColor || "#D1D5DB",
                    transform: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      customStyles.hoverBackgroundColor || "#374151";
                    e.currentTarget.style.color =
                      customStyles.hoverTextColor || "#93C5FD";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color =
                      customStyles.textColor || "#D1D5DB";
                  }}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
                >
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </span>
                </Link>
                {!user.isAdmin && (
                  <Link
                    onClick={handleLinkClick}
                    to="/orders"
                    style={{ color: customStyles.textColor || "#D1D5DB" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        customStyles.hoverBackgroundColor || "#374151";
                      e.currentTarget.style.color =
                        customStyles.hoverTextColor || "#93C5FD";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color =
                        customStyles.textColor || "#D1D5DB";
                    }}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
                  >
                    <span className="flex items-center">
                      <ShoppingBag className="w-4 h-4 mr-2" /> Orders
                    </span>
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLinkClick();
                    handleLogout();
                  }}
                  style={{ color: customStyles.textColor || "#D1D5DB" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      customStyles.hoverBackgroundColor || "#374151";
                    e.currentTarget.style.color =
                      customStyles.hoverTextColor || "#93C5FD";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color =
                      customStyles.textColor || "#D1D5DB";
                  }}
                  className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
                >
                  <span className="flex items-center">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </span>
                </button>
              </>
            ) : (
              <div className="flex flex-col space-y-1 px-3 py-2">
                <Link
                  onClick={handleLinkClick}
                  to="/login"
                  style={{
                    color: customStyles.textColor || "#D1D5DB",
                    backgroundColor:
                      customStyles.activeBackgroundColor || "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      customStyles.hoverBackgroundColor || "#374151";
                    e.currentTarget.style.color =
                      customStyles.hoverTextColor || "#93C5FD";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      customStyles.activeBackgroundColor || "transparent";
                    e.currentTarget.style.color =
                      customStyles.textColor || "#D1D5DB";
                  }}
                  className="w-full block py-2 text-center rounded-md text-white bg-blue-900 font-semibold hover:font-bold hover:bg-gray-700 hover:text-blue-400"
                >
                  Login
                </Link>
                {/* <Link 
                onClick={handleLinkClick}
                to="/register"
                style={{
                  color: customStyles.textColor || '#D1D5DB',
                  backgroundColor : customStyles.activeBackgroundColor ||'transparent'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
                  e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = customStyles.activeBackgroundColor || 'transparent';
                  e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
                }}
                className="w-full block py-2 text-center rounded-md text-white bg-blue-600 font-semibold hover:font-bold hover:bg-blue-500"
              >
                Register
              </Link>           */}
              </div>
            )}

            {/* Admin Dashboard link at the end - highlighted */}
            {user?.isAdmin && (
              <>
                <Link
                  onClick={handleLinkClick}
                  to="/pitech"
                  style={{
                    color: customStyles.textColor || "#D1D5DB",
                    backgroundColor:
                      customStyles.activeBackgroundColor || "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      customStyles.hoverBackgroundColor || "#374151";
                    e.currentTarget.style.color =
                      customStyles.hoverTextColor || "#93C5FD";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      customStyles.activeBackgroundColor || "transparent";
                    e.currentTarget.style.color =
                      customStyles.textColor || "#D1D5DB";
                  }}
                  className="w-full block py-3 mt-3 text-center rounded-md text-white bg-blue-600 hover:bg-blue-500 font-medium"
                >
                  <span className="flex items-center justify-center">
                    <UserRoundCog className="w-5 h-5 mr-2" /> Admin Dashboard
                  </span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;






// // works perfectly before saba edition
// // src/components/Navbar.js
// import React, { useState, useEffect } from "react";
// import { Link, useLocation } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import { useCart } from "../context/CartContext";
// import axios from "axios";
// import {
//   ShoppingCartIcon,
//   Menu,
//   X,
//   User,
//   Package,
//   Home,
//   Users as Info,
//   Mail,
//   LogOut,
//   UserRoundCog,
//   ShoppingBag,
//   LoaderPinwheel as Blog,
//   ExternalLink,
//   FileText,
//   Phone,
//   MessageCircle,
//   Calendar,
//   Map,
//   Bookmark,
//   Award,
//   Database,
//   Layers,
//   Coffee,
//   Gift,
//   Tag,
//   Clock,
//   CreditCard,
//   PenTool,
//   Truck,
//   Image,
//   Heart,
//   Star,
//   Settings,
//   Linkedin,
//   Twitter,
//   Facebook,
//   Instagram,
//   Youtube,
//   ChevronDown,
//   ChevronRight,
// } from "lucide-react";
// import { useConfig } from "../context/ConfigContext";

// // Map icon names to components
// const iconMap = {
//   Home: Home,
//   Package: Package,
//   Mail: Mail,
//   Users: Info,
//   Info: Info,
//   ExternalLink: ExternalLink,
//   ShoppingCart: ShoppingCartIcon,
//   User: User,
//   Settings: Settings,
//   FileText: FileText,
//   Phone: Phone,
//   MessageCircle: MessageCircle,
//   Calendar: Calendar,
//   Map: Map,
//   Bookmark: Bookmark,
//   Award: Award,
//   Database: Database,
//   Layers: Layers,
//   Coffee: Coffee,
//   Gift: Gift,
//   Tag: Tag,
//   Clock: Clock,
//   CreditCard: CreditCard,
//   PenTool: PenTool,
//   Truck: Truck,
//   Image: Image,
//   Heart: Heart,
//   Star: Star,
//   ShoppingBag: ShoppingBag,
//   Linkedin: Linkedin,
//   Twitter: Twitter,
//   Facebook: Facebook,
//   Instagram: Instagram,
//   Youtube: Youtube,
// };

// // Function to render icon based on name
// const renderIcon = (iconName) => {
//   if (!iconName) return null;

//   const IconComponent = iconMap[iconName];
//   if (IconComponent) {
//     return <IconComponent className="w-4 h-4 mr-1" />;
//   }
//   return null;
// };

// // Generate inline styles from menu styling options
// const generateItemStyles = (
//   item,
//   baseStyles,
//   isActive = false,
//   hoverState = false
// ) => {
//   const styles = { ...baseStyles };

//   // Apply menu's base styles
//   if (baseStyles) {
//     if (!hoverState && !isActive) {
//       if (baseStyles.textColor) styles.color = baseStyles.textColor;
//       if (baseStyles.backgroundColor)
//         styles.backgroundColor = baseStyles.backgroundColor;
//     } else if (hoverState && !isActive) {
//       if (baseStyles.hoverTextColor) styles.color = baseStyles.hoverTextColor;
//       if (baseStyles.hoverBackgroundColor)
//         styles.backgroundColor = baseStyles.hoverBackgroundColor;
//     } else if (isActive) {
//       if (baseStyles.activeTextColor) styles.color = baseStyles.activeTextColor;
//       if (baseStyles.activeBackgroundColor)
//         styles.backgroundColor = baseStyles.activeBackgroundColor;
//     }

//     if (baseStyles.fontFamily) styles.fontFamily = baseStyles.fontFamily;
//     if (baseStyles.fontSize) styles.fontSize = baseStyles.fontSize;
//     if (baseStyles.fontWeight) styles.fontWeight = baseStyles.fontWeight;
//     if (baseStyles.borderRadius) styles.borderRadius = baseStyles.borderRadius;
//   }

//   // Override with item-specific styles if provided
//   if (item?.styles) {
//     if (!hoverState && !isActive) {
//       if (item.styles.textColor) styles.color = item.styles.textColor;
//       if (item.styles.backgroundColor)
//         styles.backgroundColor = item.styles.backgroundColor;
//     } else if (hoverState && !isActive) {
//       if (item.styles.hoverTextColor) styles.color = item.styles.hoverTextColor;
//       if (item.styles.hoverBackgroundColor)
//         styles.backgroundColor = item.styles.hoverBackgroundColor;
//     }

//     if (item.styles.fontFamily) styles.fontFamily = item.styles.fontFamily;
//     if (item.styles.fontSize) styles.fontSize = item.styles.fontSize;
//     if (item.styles.fontWeight) styles.fontWeight = item.styles.fontWeight;
//     if (item.styles.borderRadius)
//       styles.borderRadius = item.styles.borderRadius;
//   }

//   return styles;
// };

// const Navbar = ({ menuName = "navbar", fallbackMenuName = "main" }) => {
//   const { user, logout } = useAuth();
//   const { cart } = useCart();
//   const [isOpen, setIsOpen] = useState(false);
//   const [isScrolled, setIsScrolled] = useState(false);
//   const location = useLocation();

//   const [expandedMobileItems, setExpandedMobileItems] = useState({});

//   //for centered scrolling
//   const [showLogo, setShowLogo] = useState(true);
//   const [navbarHeight, setNavbarHeight] = useState(null);
//   const [navbarOpacity, setNavbarOpacity] = useState(1);

//   // Dynamic menu state
//   const [menuData, setMenuData] = useState(null);
//   const [menuItems, setMenuItems] = useState([]);
//   const [menuLoading, setMenuLoading] = useState(true);

//   // Hover state for menu items
//   const [hoveredItem, setHoveredItem] = useState(null);

//   // Custom style states
//   const [navbarLogo, setNavbarLogo] = useState(null);
//   const [customStyles, setCustomStyles] = useState({});
//   const [navbarOptions, setNavbarOptions] = useState({});

//   // Check if current path is the admin dashboard
//   const isAdminDashboard = location.pathname === "/pitech";

//   //for phone & email shown in cenetred
//   const { config } = useConfig();
//   const contactEmail = config.contactEmail;
//   const contactPhone = config.contactPhone;

//   // Effect for handling admin dashboard specific styling
//   useEffect(() => {
//     const fixAdminDashboardPadding = () => {
//       // Fix for admin dashboard
//       if (isAdminDashboard) {
//         document.body.style.paddingTop = "40px"; // Fixed padding for admin dashboard

//         const navbar = document.querySelector("nav");
//         if (navbar) {
//           navbar.style.height = "40px"; // Fixed height for navbar on admin dashboard
//           setNavbarHeight("40px");
//           if (navbarOptions.layout === "centered") {
//             setShowLogo(true);
//           }
//         }
//       }
//     };
//     fixAdminDashboardPadding();
//   });

//   useEffect(() => {
//     // Set initial navbar positioning immediately
//     const navbar = document.querySelector("nav");
//     if (navbar) {
//       navbar.style.top = "0";
//       navbar.style.position = "fixed";
//     }
//   }, []);

//   //function to sort menu items by order
//   const sortMenuItemsByOrder = (items) => {
//     return [...items].sort((a, b) => a.order - b.order);
//   };

//   // Fetch dynamic menu items
//   useEffect(() => {
//     const fetchMenu = async () => {
//       try {
//         const token = localStorage.getItem("token");

//         const response = await axios.get(`/api/menus/name/${menuName}`);
//         const menu = response.data;

//         setMenuData(menu);

//         // Create a deep copy of the menu items array and sort by order
//         let sortedItems = [];
//         if (menu.items && menu.items.length > 0) {
//           // Create deep copy to avoid mutation issues
//           sortedItems = JSON.parse(JSON.stringify(menu.items));
//           // Sort by order property
//           sortedItems.sort((a, b) => {
//             // If order properties are the same, maintain original order
//             if (a.order === b.order) return 0;
//             // Otherwise sort by order property
//             return a.order - b.order;
//           });

//           // Also sort children of each item
//           sortedItems.forEach((item) => {
//             if (item.children && item.children.length > 0) {
//               item.children.sort((a, b) => a.order - b.order);
//             }
//           });
//         }

//         // Set the sorted items in state
//         setMenuItems(sortedItems);

//         // Extract styling information
//         if (menu.styles) {
//           setCustomStyles(menu.styles);
//         }

//         if (menu.navbarOptions) {
//           setNavbarOptions(menu.navbarOptions);
//           if (menu.navbarOptions.logo) {
//             setNavbarLogo(menu.navbarOptions.logo);
//           }
//         }

//         // Load custom CSS if provided
//         if (menu.customCss) {
//           // Create a style element for custom CSS
//           const styleElement = document.createElement("style");
//           styleElement.id = `menu-custom-css-${menu._id}`;
//           styleElement.textContent = menu.customCss;
//           document.head.appendChild(styleElement);

//           // Cleanup function
//           return () => {
//             const existingStyle = document.getElementById(
//               `menu-custom-css-${menu._id}`
//             );
//             if (existingStyle) {
//               document.head.removeChild(existingStyle);
//             }
//           };
//         }
//       } catch (error) {
//         console.error("Error fetching menu:", error);
//       } finally {
//         setMenuLoading(false);
//       }
//     };

//     fetchMenu();
//   }, [menuName]);

//   const toggleMobileItem = (itemId) => {
//     setExpandedMobileItems((prev) => ({
//       ...prev,
//       [itemId]: !prev[itemId],
//     }));
//   };

//   // Generate CSS classes based on configuration
//   const getNavbarClasses = () => {
//     const baseClasses = "fixed w-full z-50 transition-all duration-300";

//     // Add background color classes based on scroll state
//     const bgClasses = isScrolled
//       ? "bg-gray-900 shadow-lg py-2"
//       : "bg-gray-800/90 backdrop-blur-sm py-4";

//     return `${baseClasses} ${bgClasses} ${customStyles.customClasses || ""}`;
//   };

//   // Generate container class based on navbarOptions
//   const getContainerClasses = () => {
//     let containerClass = "w-full px-4 sm:px-6 lg:px-8";

//     if (
//       navbarOptions.containerMaxWidth &&
//       navbarOptions.containerMaxWidth !== "100%"
//     ) {
//       containerClass += " mx-auto";
//     }

//     return containerClass;
//   };

//   function darkenColor(hex, amount = 20) {
//     let col = hex.startsWith("#") ? hex.slice(1) : hex;
//     if (col.length === 3)
//       col = col
//         .split("")
//         .map((c) => c + c)
//         .join("");

//     const num = parseInt(col, 16);
//     const r = Math.max(0, (num >> 16) - amount);
//     const g = Math.max(0, ((num >> 8) & 0x00ff) - amount);
//     const b = Math.max(0, (num & 0x0000ff) - amount);

//     return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
//   }

//   // Render menu items dynamically
//   const renderMenuItems = (items) => {
//     return items.map((item) => {
//       // Skip items that should not be shown on this device
//       if (
//         (item.mobileOnly &&
//           window.innerWidth >=
//             parseInt(navbarOptions.mobileBreakpoint || "768px")) ||
//         (item.desktopOnly &&
//           window.innerWidth <
//             parseInt(navbarOptions.mobileBreakpoint || "768px"))
//       ) {
//         return null;
//       }

//       const isActive = location.pathname === item.path;
//       const itemClasses = `px-2 py-2  rounded-md text-sm font-medium ${
//         isActive
//           ? "text-blue-400 bg-gray-700"
//           : "text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//       } transition-all duration-200 ${item.customClasses || ""}`;

//       // Generate dynamic styles
//       const itemStyles = generateItemStyles(item, customStyles, isActive);
//       const hoverStyles = generateItemStyles(
//         item,
//         customStyles,
//         isActive,
//         true
//       );

//       return (
//         <React.Fragment key={item._id}>
//           {/* Render child items only if they exist */}
//           {item.children && item.children.length > 0 ? (
//             <div className="group relative inline-block hover:delay-150">
//               {/* Parent menu item */}
//               <div
//                 className={`px-3 py-2 mr-4 rounded-md text-sm font-medium ${
//                   location.pathname === item.path
//                     ? "text-blue-400 bg-gray-700"
//                     : "text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//                 } transition-all duration-200 cursor-pointer flex items-center ${
//                   item.customClasses || ""
//                 }`}
//                 style={hoveredItem === item._id ? hoverStyles : itemStyles}
//                 onMouseEnter={() => setHoveredItem(item._id)}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 {item.icon && renderIcon(item.icon)}
//                 {item.title}
//                 <ChevronDown className="w-3 h-3 ml-1" />
//               </div>

//               {/* Dropdown for children */}
//               <div className="absolute left-0 -mt-2 pt-6 hidden group-hover:block bg-gray-800 rounded-md shadow-lg py-1 min-w-max z-50 transition-opacity duration-300 group-hover:opacity-100 opacity-0 group-hover:delay-0">
//                 {item.children.map((child) => {
//                   // Skip children based on display settings
//                   if (
//                     (child.mobileOnly &&
//                       window.innerWidth >=
//                         parseInt(navbarOptions.mobileBreakpoint || "768px")) ||
//                     (child.desktopOnly &&
//                       window.innerWidth <
//                         parseInt(navbarOptions.mobileBreakpoint || "768px"))
//                   ) {
//                     return null;
//                   }

//                   const isChildActive = location.pathname === child.path;
//                   const childStyles = generateItemStyles(
//                     child,
//                     customStyles,
//                     isChildActive
//                   );
//                   const childHoverStyles = generateItemStyles(
//                     child,
//                     customStyles,
//                     isChildActive,
//                     true
//                   );

//                   return (
//                     <React.Fragment key={child._id}>
//                       {child.isExternal ? (
//                         <a
//                           href={child.path}
//                           target="_blank"
//                           rel="noopener noreferrer"
//                           className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400 whitespace-nowrap ${
//                             child.customClasses || ""
//                           }`}
//                           style={
//                             hoveredItem === child._id
//                               ? childHoverStyles
//                               : childStyles
//                           }
//                           onMouseEnter={() => setHoveredItem(child._id)}
//                           onMouseLeave={() => setHoveredItem(null)}
//                         >
//                           <span className="flex items-center">
//                             {child.icon && renderIcon(child.icon)}
//                             {child.title}
//                             {!child.icon && (
//                               <ExternalLink className="w-3 h-3 ml-1" />
//                             )}
//                           </span>
//                         </a>
//                       ) : (
//                         <Link
//                           to={child.path}
//                           onClick={showPageLoading}
//                           className={`block px-4 py-2 text-sm ${
//                             location.pathname === child.path
//                               ? "text-blue-400 bg-gray-700"
//                               : "text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           } whitespace-nowrap ${child.customClasses || ""}`}
//                           style={
//                             hoveredItem === child._id
//                               ? childHoverStyles
//                               : childStyles
//                           }
//                           onMouseEnter={() => setHoveredItem(child._id)}
//                           onMouseLeave={() => setHoveredItem(null)}
//                         >
//                           <span className="flex items-center">
//                             {child.icon && renderIcon(child.icon)}
//                             {child.title}
//                           </span>
//                         </Link>
//                       )}
//                     </React.Fragment>
//                   );
//                 })}
//               </div>
//             </div>
//           ) : // Regular menu item with no children
//           item.isExternal ? (
//             <a
//               href={item.path}
//               target="_blank"
//               rel="noopener noreferrer"
//               className={itemClasses}
//               style={hoveredItem === item._id ? hoverStyles : itemStyles}
//               onMouseEnter={() => setHoveredItem(item._id)}
//               onMouseLeave={() => setHoveredItem(null)}
//             >
//               <span className="flex items-center">
//                 {item.icon && renderIcon(item.icon)}
//                 {item.title}
//                 {!item.icon && <ExternalLink className="w-3 h-3 ml-1" />}
//               </span>
//             </a>
//           ) : (
//             <Link
//               to={item.path}
//               onClick={showPageLoading}
//               className={itemClasses}
//               style={hoveredItem === item._id ? hoverStyles : itemStyles}
//               onMouseEnter={() => setHoveredItem(item._id)}
//               onMouseLeave={() => setHoveredItem(null)}
//             >
//               <span className="flex items-center">
//                 {item.icon && renderIcon(item.icon)}
//                 {item.title}
//               </span>
//             </Link>
//           )}
//         </React.Fragment>
//       );
//     });
//   };

//   // Function to render mobile menu items recursively
//   const renderMobileMenuItems = (items, depth = 0) => {
//     return items.map((item) => {
//       // Skip items that should not be shown on mobile
//       if (item.desktopOnly) {
//         return null;
//       }

//       const isActive = location.pathname === item.path;
//       const itemStyles = generateItemStyles(item, customStyles, isActive);

//       return (
//         <React.Fragment key={item._id}>
//           {item.children && item.children.length > 0 ? (
//             <div>
//               <div
//                 className={`block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700 ${
//                   depth > 0 ? "pl-8" : ""
//                 } cursor-pointer ${item.customClasses || ""}`}
//                 onClick={() => toggleMobileItem(item._id)}
//                 style={itemStyles}
//               >
//                 <span className="flex items-center justify-between">
//                   <span className="flex items-center">
//                     {item.icon && renderIcon(item.icon)}
//                     {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
//                     {item.title}
//                   </span>
//                   <ChevronDown
//                     className={`w-4 h-4 transition-transform duration-200 ${
//                       expandedMobileItems[item._id] ? "rotate-180" : ""
//                     }`}
//                   />
//                 </span>
//               </div>

//               <div
//                 className={`ml-2 border-l border-gray-700 overflow-hidden transition-all duration-300 ${
//                   expandedMobileItems[item._id] ? "max-h-96" : "max-h-0"
//                 }`}
//               >
//                 {renderMobileMenuItems(
//                   item.children.filter((child) => !child.desktopOnly),
//                   depth + 1
//                 )}
//               </div>
//             </div>
//           ) : item.isExternal ? (
//             <a
//               href={item.path}
//               target="_blank"
//               rel="noopener noreferrer"
//               className={`block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700 ${
//                 depth > 0 ? "pl-8" : ""
//               } ${item.customClasses || ""}`}
//               onClick={handleLinkClick}
//               style={itemStyles}
//             >
//               <span className="flex items-center">
//                 {item.icon && renderIcon(item.icon)}
//                 {!item.icon && depth === 0 && (
//                   <ExternalLink className="w-4 h-4 mr-2" />
//                 )}
//                 {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
//                 {item.title}
//               </span>
//             </a>
//           ) : (
//             <Link
//               to={item.path}
//               onClick={handleLinkClick}
//               className={`block px-3 py-2 rounded-md text-base font-medium ${
//                 location.pathname === item.path
//                   ? "text-blue-400 bg-gray-700"
//                   : "text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//               } ${depth > 0 ? "pl-8" : ""} ${item.customClasses || ""}`}
//               style={itemStyles}
//             >
//               <span className="flex items-center">
//                 {item.icon && renderIcon(item.icon)}
//                 {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
//                 {item.title}
//               </span>
//             </Link>
//           )}
//         </React.Fragment>
//       );
//     });
//   };

//   // scroll effect
//   useEffect(() => {
//     const handleScroll = () => {
//       if (window.scrollY > 10) {
//         setIsScrolled(true);
//         setNavbarOpacity(0.95); // Set opacity when scrolled
//         // In centered layout when scrolled
//         if (navbarOptions.layout === "centered") {
//           setShowLogo(false);
//           setNavbarHeight("80px"); // Set fixed height when scrolled
//         }
//       } else {
//         setIsScrolled(false);
//         setNavbarOpacity(1); // Reset to full opacity when at top
//         setShowLogo(true);
//         // Reset to original height from options
//         setNavbarHeight(
//           window.innerWidth >
//             parseInt(navbarOptions.mobileBreakpoint || "768px")
//             ? navbarOptions.heightDesktop || "80px"
//             : navbarOptions.heightMobile || "70px"
//         );
//       }
//     };

//     // Initialize the height and opacity
//     setNavbarHeight(
//       window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || "768px")
//         ? navbarOptions.heightDesktop || "80px"
//         : navbarOptions.heightMobile || "70px"
//     );

//     window.addEventListener("scroll", handleScroll);
//     return () => window.removeEventListener("scroll", handleScroll);
//   }, [
//     navbarOptions.layout,
//     navbarOptions.heightDesktop,
//     navbarOptions.heightMobile,
//     navbarOptions.mobileBreakpoint,
//   ]);

//   useEffect(() => {
//     // Get navbar height from options or use defaults
//     const desktopHeight = navbarOptions.heightDesktop || "80px";
//     const mobileHeight = navbarOptions.heightMobile || "70px";
//     const mobileBreakpoint = navbarOptions.mobileBreakpoint || "768px";

//     // Convert px values to numbers for calculations
//     const getPixelValue = (value) => {
//       if (typeof value === "string") {
//         return parseInt(value) || (value.includes("px") ? parseInt(value) : 80);
//       }
//       return value || 80;
//     };

//     // Set the correct height directly on the navbar element
//     const navbar = document.querySelector("nav");
//     if (navbar) {
//       // Remove negative margins and set position instead
//       navbar.style.marginTop = "0";
//       navbar.style.top = "0";

//       if (window.innerWidth > parseInt(mobileBreakpoint)) {
//         navbar.style.height = `${getPixelValue(desktopHeight)}px`;
//       } else {
//         navbar.style.height = `${getPixelValue(mobileHeight)}px`;
//       }
//     }

//     // Add padding to body to match the navbar height
//     document.body.style.paddingTop =
//       window.innerWidth > parseInt(mobileBreakpoint)
//         ? `${getPixelValue(desktopHeight)}px`
//         : `${getPixelValue(mobileHeight)}px`;

//     // Add media query for responsive padding
//     const mediaQuery = window.matchMedia(`(max-width: ${mobileBreakpoint})`);
//     const handleMediaChange = (e) => {
//       if (e.matches) {
//         document.body.style.paddingTop = `${getPixelValue(mobileHeight)}px`;
//         if (navbar) navbar.style.height = `${getPixelValue(mobileHeight)}px`;
//       } else {
//         document.body.style.paddingTop = `${getPixelValue(desktopHeight)}px`;
//         if (navbar) navbar.style.height = `${getPixelValue(desktopHeight)}px`;
//       }
//     };

//     // Set initial padding based on screen size
//     handleMediaChange(mediaQuery);

//     // Add listener for screen size changes
//     mediaQuery.addEventListener("change", handleMediaChange);

//     // Clean up
//     return () => {
//       mediaQuery.removeEventListener("change", handleMediaChange);
//       document.body.style.paddingTop = "0";
//     };
//   }, [navbarOptions]);

//   const toggleMenu = () => setIsOpen(!isOpen);

//   // Function to show loading screen
//   const showPageLoading = () => {
//     document.body.classList.add("page-loading");

//     // Automatically remove loading after a short timeout (for demo)
//     setTimeout(() => {
//       document.body.classList.remove("page-loading");
//     }, 600);
//   };

//   // Function to close menu when a mobile link is clicked
//   const handleLinkClick = () => {
//     setIsOpen(false);
//   };

//   const handleLogout = () => {
//     showPageLoading();
//     logout();
//     // Redirect to home or login page after logout
//   };

//   // Get navbar layout style
//   const getNavbarLayout = () => {
//     const layout = navbarOptions.layout || "standard";

//     switch (layout) {
//       case "centered":
//         return "flex-col items-center";
//       case "split":
//         return "justify-between items-center";
//       default: // standard
//         return "justify-between items-center";
//     }
//   };

//   if (menuLoading) {
//     return (
//       <div
//         className="fixed w-full h-16 z-50"
//         style={{
//           backgroundColor: "transparent",
//           transition: "background-color 0.3s ease",
//         }}
//       ></div>
//     );
//   }

//   return (
//     <nav
//       className={getNavbarClasses()}
//       style={{
//         top: "0",
//         position: "fixed",
//         height: isAdminDashboard
//           ? "80px"
//           : navbarHeight ||
//             (window.innerWidth >
//             parseInt(navbarOptions.mobileBreakpoint || "768px")
//               ? navbarOptions.heightDesktop || "80px"
//               : navbarOptions.heightMobile || "70px"),

//         // height: navbarHeight || (window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || '768px')
//         //   ? navbarOptions.heightDesktop || '80px'
//         //   : navbarOptions.heightMobile || '70px'),
//         // opacity: navbarOpacity,
//         opacity: isAdminDashboard ? 1 : navbarOpacity,
//         transition: "height 0.3s ease, opacity 0.3s ease",
//         ...(customStyles.backgroundColor && {
//           backgroundColor: customStyles.backgroundColor,
//         }),
//         ...(navbarOptions.containerMaxWidth && {
//           maxWidth: navbarOptions.containerMaxWidth,
//         }),
//       }}
//     >
//       <div
//         className={getContainerClasses()}
//         style={{
//           height: "100%", // Make container take full height
//           ...(navbarOptions.containerMaxWidth
//             ? { maxWidth: navbarOptions.containerMaxWidth }
//             : {}),
//         }}
//       >
//         {/* Mobile layout with hamburger-logo-cart */}
//         <div className="flex items-center justify-between w-full md:hidden h-full">
//           <div className="flex-1 flex justify-start">
//             {!isAdminDashboard && (
//               <button
//                 onClick={toggleMenu}
//                 className="p-1 rounded-md focus:outline-none hover:bg-gray-700 transition-all duration-200"
//                 style={{
//                   boxShadow: "none",
//                   border: "none",
//                   background: "transparent",
//                   transform: "none",
//                   padding: 0,
//                   overflow: "visible",
//                 }}
//               >
//                 {isOpen ? (
//                   <X
//                     className="w-6 h-6 text-gray-300"
//                     style={{ color: customStyles.activeBackgroundColor }}
//                   />
//                 ) : (
//                   <Menu
//                     className="w-6 h-6 text-gray-300"
//                     style={{ color: customStyles.activeBackgroundColor }}
//                   />
//                 )}
//               </button>
//             )}
//           </div>

//           {/* Logo - center on mobile */}
//           <div className="flex-1 flex items-center justify-center">
//             <Link
//               to="/"
//               className="flex items-center h-full"
//               onClick={showPageLoading}
//             >
//               {navbarLogo ? (
//                 <img
//                   className="transition-all duration-300 hover:scale-105"
//                   src={navbarLogo}
//                   alt="Logo"
//                   style={{
//                     maxHeight: navbarOptions.logoHeight || "60px",
//                     width: navbarOptions.logoWidth || "auto",
//                   }}
//                 />
//               ) : (
//                 // Fallback logo or text if no logo is set
//                 <div className="text-xl font-bold text-white">LOGO</div>
//               )}
//             </Link>
//           </div>

//           {/* Cart - right on mobile */}
//           <div className="flex-1 flex justify-end">
//             <Link
//               to="/cart"
//               className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//               onClick={(e) => {
//                 e.preventDefault();
//                 window.location.href = "/cart";
//               }}
//             >
//               <ShoppingCartIcon
//                 style={{ color: customStyles.activeBackgroundColor }}
//                 className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
//               />
//               {cart.length > 0 && (
//                 <span
//                   className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                   style={{
//                     backgroundColor:
//                       customStyles.activeBackgroundColor || "#2563EB",
//                   }}
//                 >
//                   {cart.length}
//                 </span>
//               )}
//             </Link>
//           </div>
//         </div>

//         {/* Desktop Layout - based on navbarOptions.layout */}
//         <div className="hidden md:flex h-full w-full items-center">
//           {navbarOptions.layout === "centered" ? (
//             /* Centered Layout (Logo top, menu center) */
//             <div className="flex flex-col w-full h-full justify-center items-center">
//               {/* {navbarOptions.layout === 'centered' && navbarOptions.showContactInfo && (
//               <div className="absolute top-3 left-1 right-1 w-full flex justify-between items-center text-xs px-4"
//                   style={{ height: '24px' }}>
//                 <div className="flex items-center">
//                   {contactPhone && (
//                     <a href={`tel:${contactPhone}`}
//                     style={{color: customStyles.textColor || '#D1D5DB'}}
//                     className="flex items-center text-md transition-colors">
//                       <Phone className="w-4 h-4 mr-1" />
//                       <span style={{fontSize: 16+'px'}}>{contactPhone}</span>
//                     </a>
//                   )}
//                 </div>
//                 <div className="flex items-center">
//                   {contactEmail && (
//                     <a href={`mailto:${contactEmail}`}
//                     style={{color: customStyles.textColor || '#D1D5DB'}}
//                     className="flex items-center text-md transition-colors">
//                       <Mail className="w-4 h-4 mr-1" />
//                       <span style={{fontSize: 16+'px'}}>{contactEmail}</span>
//                     </a>
//                   )}
//                 </div>
//               </div>
//             )} */}
//               {navbarOptions.layout === "centered" &&
//                 navbarOptions.showContactInfo && (
//                   <div
//                     className="absolute left-1 right-1 w-full flex justify-between items-center text-xs px-4"
//                     style={{
//                       height: "24px",
//                       top: "50%",
//                       transform: "translateY(-50%)",
//                     }}
//                   >
//                     <div className="flex items-center">
//                       {contactPhone && (
//                         <a
//                           href={`tel:${contactPhone}`}
//                           style={{ color: customStyles.textColor || "#D1D5DB" }}
//                           className="flex items-center text-md transition-colors"
//                         >
//                           <Phone className="w-4 h-4 mr-1" />
//                           <span style={{ fontSize: 16 + "px" }}>
//                             {contactPhone}
//                           </span>
//                         </a>
//                       )}
//                     </div>
//                     <div className="flex items-center">
//                       {contactEmail && (
//                         <a
//                           href={`mailto:${contactEmail}`}
//                           style={{ color: customStyles.textColor || "#D1D5DB" }}
//                           className="flex items-center text-md transition-colors"
//                         >
//                           <Mail className="w-4 h-4 mr-1" />
//                           <span style={{ fontSize: 16 + "px" }}>
//                             {contactEmail}
//                           </span>
//                         </a>
//                       )}
//                     </div>
//                   </div>
//                 )}

//               {/* Logo on top - only shown when not scrolled */}
//               {showLogo && (
//                 <div
//                   className="flex justify-center mb-2 transition-all duration-300"
//                   style={{
//                     display: isAdminDashboard ? "none" : "block",
//                   }}
//                 >
//                   <Link
//                     to="/"
//                     className="flex items-center"
//                     onClick={showPageLoading}
//                   >
//                     {navbarLogo ? (
//                       <img
//                         className="transition-all duration-300 hover:scale-105"
//                         src={navbarLogo}
//                         alt="Logo"
//                         style={{
//                           maxHeight: navbarOptions.logoHeight || "60px",
//                           width: navbarOptions.logoWidth || "auto",
//                         }}
//                       />
//                     ) : (
//                       <div className="text-xl font-bold text-white">LOGO</div>
//                     )}
//                   </Link>
//                 </div>
//               )}

//               {/* Menu items centered */}
//               <div
//                 className="flex justify-center items-center space-x-2"
//                 style={{
//                   position: "relative",
//                   zIndex: isAdminDashboard ? 20 : 5, // Higher z-index in admin dashboard
//                 }}
//               >
//                 {!menuLoading && renderMenuItems(menuItems)}

//                 {/* <Link to="/blog"
//                 onClick={showPageLoading}
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
//                 style={location.pathname === '/blog' ?
//                   generateItemStyles(null, customStyles, true) :
//                   generateItemStyles(null, customStyles)}
//                 onMouseEnter={() => setHoveredItem('blog')}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
//               </Link> */}

//                 {navbarOptions.layout === "centered" &&
//                   navbarOptions.showContactInfo &&
//                   navbarOptions.contactPosition === "inline" && (
//                     <div
//                       className="flex items-center space-x-2"
//                       style={{
//                         position: "absolute",
//                         top: "50%",
//                         transform: "translateY(-50%)",
//                         display: "flex",
//                         justifyContent: "center",
//                         width: "100%",
//                       }}
//                     >
//                       {contactPhone && (
//                         <a
//                           href={`tel:${contactPhone}`}
//                           className="px-3 py-2 rounded-md text-md font-medium transition-all duration-200"
//                           style={
//                             hoveredItem === "phone"
//                               ? generateItemStyles(
//                                   null,
//                                   customStyles,
//                                   false,
//                                   true
//                                 )
//                               : generateItemStyles(null, customStyles)
//                           }
//                           onMouseEnter={() => setHoveredItem("phone")}
//                           onMouseLeave={() => setHoveredItem(null)}
//                         >
//                           <span
//                             style={{ fontSize: 16 + "px" }}
//                             className="flex items-center"
//                           >
//                             <Phone className="w-6 h-6 mr-1" /> {contactPhone}
//                           </span>
//                         </a>
//                       )}
//                       {contactEmail && (
//                         <a
//                           href={`mailto:${contactEmail}`}
//                           className="px-3 py-2 rounded-md text-md font-medium transition-all duration-200"
//                           style={
//                             hoveredItem === "email"
//                               ? generateItemStyles(
//                                   null,
//                                   customStyles,
//                                   false,
//                                   true
//                                 )
//                               : generateItemStyles(null, customStyles)
//                           }
//                           onMouseEnter={() => setHoveredItem("email")}
//                           onMouseLeave={() => setHoveredItem(null)}
//                         >
//                           <span
//                             style={{ fontSize: 16 + "px" }}
//                             className="flex items-center"
//                           >
//                             <Mail className="w-6 h-6 mr-1" /> {contactEmail}
//                           </span>
//                         </a>
//                       )}
//                     </div>
//                   )}

//                 {/* User and cart buttons */}
//                 <div className="flex items-center space-x-2">
//                   <div className="relative">
//                     {user ? (
//                       <div className="flex items-center group">
//                         <button
//                           className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
//                           style={{
//                             transform: "none",
//                             boxShadow: "none",
//                             border: "none",
//                             color: customStyles.textColor || "#D1D5DB",
//                           }}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               customStyles.hoverBackgroundColor || "#374151";
//                             e.currentTarget.style.color =
//                               customStyles.hoverTextColor || "#93C5FD";
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               "transparent";
//                             e.currentTarget.style.color =
//                               customStyles.textColor || "#D1D5DB";
//                           }}
//                         >
//                           <User className="w-5 h-5" />
//                           <span className="text-sm font-medium">
//                             {user.firstName || "Account"}
//                           </span>
//                         </button>

//                         {/* Dropdown menu */}
//                         <div
//                           className="absolute left-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                           style={{
//                             backgroundColor:
//                               customStyles.backgroundColor || "#1F2937",
//                             borderRadius:
//                               // customStyles.borderRadius ||
//                               "0.375rem",
//                           }}
//                         >
//                           {user.isAdmin && (
//                             <Link
//                               to="/pitech"
//                               className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                               style={{
//                                 color: customStyles.textColor || "#D1D5DB",
//                               }}
//                               onMouseEnter={(e) => {
//                                 e.currentTarget.style.backgroundColor =
//                                   customStyles.hoverBackgroundColor ||
//                                   "#374151";
//                                 e.currentTarget.style.color =
//                                   customStyles.hoverTextColor || "#93C5FD";
//                               }}
//                               onMouseLeave={(e) => {
//                                 e.currentTarget.style.backgroundColor =
//                                   "transparent";
//                                 e.currentTarget.style.color =
//                                   customStyles.textColor || "#D1D5DB";
//                               }}
//                             >
//                               Admin Dashboard
//                             </Link>
//                           )}
//                           <Link
//                             to="/profile"
//                             onClick={showPageLoading}
//                             className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                             style={{
//                               color: customStyles.textColor || "#D1D5DB",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 customStyles.hoverBackgroundColor || "#374151";
//                               e.currentTarget.style.color =
//                                 customStyles.hoverTextColor || "#93C5FD";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 "transparent";
//                               e.currentTarget.style.color =
//                                 customStyles.textColor || "#D1D5DB";
//                             }}
//                           >
//                             My Profile
//                           </Link>
//                           {!user.isAdmin && (
//                             <Link
//                               to="/orders"
//                               className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                               style={{
//                                 color: customStyles.textColor || "#D1D5DB",
//                               }}
//                               onMouseEnter={(e) => {
//                                 e.currentTarget.style.backgroundColor =
//                                   customStyles.hoverBackgroundColor ||
//                                   "#374151";
//                                 e.currentTarget.style.color =
//                                   customStyles.hoverTextColor || "#93C5FD";
//                               }}
//                               onMouseLeave={(e) => {
//                                 e.currentTarget.style.backgroundColor =
//                                   "transparent";
//                                 e.currentTarget.style.color =
//                                   customStyles.textColor || "#D1D5DB";
//                               }}
//                             >
//                               Orders
//                             </Link>
//                           )}
//                           <button
//                             onClick={handleLogout}
//                             style={{
//                               color: customStyles.textColor || "#D1D5DB",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 customStyles.hoverBackgroundColor || "#374151";
//                               e.currentTarget.style.color =
//                                 customStyles.hoverTextColor || "#93C5FD";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 "transparent";
//                               e.currentTarget.style.color =
//                                 customStyles.textColor || "#D1D5DB";
//                             }}
//                             className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           >
//                             <span className="flex items-center">
//                               <LogOut className="w-4 h-4 mr-1" /> Logout
//                             </span>
//                           </button>
//                         </div>
//                       </div>
//                     ) : (
//                       <div className="flex items-center space-x-2">
//                         <Link
//                           to="/login"
//                           style={
//                             hoveredItem === "login"
//                               ? generateItemStyles(
//                                   null,
//                                   customStyles,
//                                   false,
//                                   true
//                                 )
//                               : generateItemStyles(null, customStyles)
//                           }
//                           onMouseEnter={() => setHoveredItem("login")}
//                           onMouseLeave={() => setHoveredItem(null)}
//                           className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
//                         >
//                           Login
//                         </Link>
//                         {/* <Link to="/register"
//                        style={hoveredItem === 'logout' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('logout')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
//                         Register
//                       </Link> */}
//                       </div>
//                     )}
//                   </div>

//                   <Link
//                     to="/cart"
//                     className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//                     onClick={(e) => {
//                       e.preventDefault();
//                       window.location.href = "/cart";
//                     }}
//                   >
//                     <ShoppingCartIcon
//                       style={{ color: customStyles.activeBackgroundColor }}
//                       className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
//                     />
//                     {cart.length > 0 && (
//                       <span
//                         className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                         style={{
//                           backgroundColor:
//                             customStyles.activeBackgroundColor || "#2563EB",
//                         }}
//                       >
//                         {cart.length}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </div>
//             </div>
//           ) : navbarOptions.layout === "split" ? (
//             /* Split Layout (Logo center, menu on sides) */
//             <div className="flex justify-between items-center w-full h-full">
//               {/* Left menu items */}
//               <div className="flex items-center space-x-2">
//                 {!menuLoading &&
//                   menuItems
//                     .slice(0, Math.ceil(menuItems.length / 2))
//                     .map((item, idx) => (
//                       <React.Fragment key={item._id || idx}>
//                         {renderMenuItems([item])}
//                       </React.Fragment>
//                     ))}
//               </div>

//               {/* Center logo */}
//               <div className="flex justify-center">
//                 <Link
//                   to="/"
//                   className="flex items-center"
//                   onClick={showPageLoading}
//                 >
//                   {navbarLogo ? (
//                     <img
//                       className="transition-all duration-300 hover:scale-105"
//                       src={navbarLogo}
//                       alt="Logo"
//                       style={{
//                         maxHeight: navbarOptions.logoHeight || "60px",
//                         width: navbarOptions.logoWidth || "auto",
//                       }}
//                     />
//                   ) : (
//                     <div className="text-xl font-bold text-white">LOGO</div>
//                   )}
//                 </Link>
//               </div>

//               {/* Right menu items + user/cart */}
//               <div className="flex items-center space-x-2">
//                 {!menuLoading &&
//                   menuItems
//                     .slice(Math.ceil(menuItems.length / 2))
//                     .map((item, idx) => (
//                       <React.Fragment key={item._id || idx}>
//                         {renderMenuItems([item])}
//                       </React.Fragment>
//                     ))}

//                 {/* <Link to="/blog"
//                 onClick={showPageLoading}
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
//                 style={location.pathname === '/blog' ?
//                   generateItemStyles(null, customStyles, true) :
//                   generateItemStyles(null, customStyles)}
//                 onMouseEnter={() => setHoveredItem('blog')}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
//               </Link> */}

//                 {/* User and Cart */}
//                 <div className="flex items-center space-x-2">
//                   {user ? (
//                     <div className="flex items-center group relative">
//                       <button
//                         className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
//                         style={{
//                           transform: "none",
//                           boxShadow: "none",
//                           border: "none",
//                           color: customStyles.textColor || "#D1D5DB",
//                         }}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor =
//                             customStyles.hoverBackgroundColor || "#374151";
//                           e.currentTarget.style.color =
//                             customStyles.hoverTextColor || "#93C5FD";
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = "transparent";
//                           e.currentTarget.style.color =
//                             customStyles.textColor || "#D1D5DB";
//                         }}
//                       >
//                         <User className="w-5 h-5" />
//                         <span className="text-sm font-medium">
//                           {user.firstName || "Account"}
//                         </span>
//                       </button>

//                       {/* Dropdown menu */}
//                       {/* <div className="absolute right-0 mt-2 top-full w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                       style={{
//                         backgroundColor: customStyles.backgroundColor || '#1F2937',
//                         borderRadius: customStyles.borderRadius || '0.375rem'
//                       }}
//                     > */}
//                       <div
//                         className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                         style={{
//                           backgroundColor:
//                             customStyles.backgroundColor || "#1F2937",
//                           borderRadius:
//                             // customStyles.borderRadius ||
//                             "0.375rem",
//                         }}
//                       >
//                         {user.isAdmin && (
//                           <Link
//                             to="/pitech"
//                             className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                             style={{
//                               color: customStyles.textColor || "#D1D5DB",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 customStyles.hoverBackgroundColor || "#374151";
//                               e.currentTarget.style.color =
//                                 customStyles.hoverTextColor || "#93C5FD";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 "transparent";
//                               e.currentTarget.style.color =
//                                 customStyles.textColor || "#D1D5DB";
//                             }}
//                           >
//                             Admin Dashboard
//                           </Link>
//                         )}
//                         <Link
//                           to="/profile"
//                           onClick={showPageLoading}
//                           className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           style={{ color: customStyles.textColor || "#D1D5DB" }}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               customStyles.hoverBackgroundColor || "#374151";
//                             e.currentTarget.style.color =
//                               customStyles.hoverTextColor || "#93C5FD";
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               "transparent";
//                             e.currentTarget.style.color =
//                               customStyles.textColor || "#D1D5DB";
//                           }}
//                         >
//                           My Profile
//                         </Link>
//                         {!user.isAdmin && (
//                           <Link
//                             to="/orders"
//                             className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                             style={{
//                               color: customStyles.textColor || "#D1D5DB",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 customStyles.hoverBackgroundColor || "#374151";
//                               e.currentTarget.style.color =
//                                 customStyles.hoverTextColor || "#93C5FD";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 "transparent";
//                               e.currentTarget.style.color =
//                                 customStyles.textColor || "#D1D5DB";
//                             }}
//                           >
//                             Orders
//                           </Link>
//                         )}
//                         <button
//                           onClick={handleLogout}
//                           style={{ color: customStyles.textColor || "#D1D5DB" }}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               customStyles.hoverBackgroundColor || "#374151";
//                             e.currentTarget.style.color =
//                               customStyles.hoverTextColor || "#93C5FD";
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               "transparent";
//                             e.currentTarget.style.color =
//                               customStyles.textColor || "#D1D5DB";
//                           }}
//                           className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         >
//                           <span className="flex items-center">
//                             <LogOut className="w-4 h-4 mr-1" /> Logout
//                           </span>
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex items-center space-x-1">
//                       <Link
//                         to="/login"
//                         style={
//                           hoveredItem === "login"
//                             ? generateItemStyles(
//                                 null,
//                                 customStyles,
//                                 false,
//                                 true
//                               )
//                             : generateItemStyles(null, customStyles)
//                         }
//                         onMouseEnter={() => setHoveredItem("login")}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
//                       >
//                         Login
//                       </Link>
//                       {/* <Link to="/register"
//                         style={hoveredItem === 'logout' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('logout')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
//                         Register
//                     </Link> */}
//                     </div>
//                   )}

//                   <Link
//                     to="/cart"
//                     className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//                     onClick={(e) => {
//                       e.preventDefault();
//                       window.location.href = "/cart";
//                     }}
//                   >
//                     <ShoppingCartIcon
//                       style={{ color: customStyles.activeBackgroundColor }}
//                       className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
//                     />
//                     {cart.length > 0 && (
//                       <span
//                         className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                         style={{
//                           backgroundColor:
//                             customStyles.activeBackgroundColor || "#2563EB",
//                         }}
//                       >
//                         {cart.length}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </div>
//             </div>
//           ) : (
//             /* Standard Layout (Logo left, menu center, actions right) */
//             <div className="flex justify-between items-center w-full h-full">
//               {/* Logo left */}
//               <div className="flex-shrink-0">
//                 <Link
//                   to="/"
//                   className="flex items-center"
//                   onClick={showPageLoading}
//                 >
//                   {navbarLogo ? (
//                     <img
//                       className="transition-all duration-300 hover:scale-105"
//                       src={navbarLogo}
//                       alt="Logo"
//                       style={{
//                         maxHeight: navbarOptions.logoHeight || "60px",
//                         width: navbarOptions.logoWidth || "auto",
//                       }}
//                     />
//                   ) : (
//                     <div className="text-xl font-bold text-white">LOGO</div>
//                   )}
//                 </Link>
//               </div>

//               {/* Menu center */}
//               <div className="flex-grow flex justify-center items-center space-x-0">
//                 {!menuLoading && renderMenuItems(menuItems)}
//                 {/* <Link to="/blog"
//                 onClick={showPageLoading}
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
//                 style={location.pathname === '/blog' ?
//                   generateItemStyles(null, customStyles, true) :
//                   generateItemStyles(null, customStyles)}
//                 onMouseEnter={() => setHoveredItem('blog')}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
//               </Link> */}
//               </div>

//               {/* Actions right */}
//               <div className="flex items-center space-x-2">
//                 <div className="relative">
//                   {user ? (
//                     <div className="flex items-center group">
//                       <button
//                         className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
//                         style={{
//                           transform: "none",
//                           boxShadow: "none",
//                           border: "none",
//                           color: customStyles.textColor || "#D1D5DB",
//                         }}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor =
//                             customStyles.hoverBackgroundColor || "#374151";
//                           e.currentTarget.style.color =
//                             customStyles.hoverTextColor || "#93C5FD";
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = "transparent";
//                           e.currentTarget.style.color =
//                             customStyles.textColor || "#D1D5DB";
//                         }}
//                       >
//                         <User className="w-5 h-5" />
//                         <span className="text-sm font-medium">
//                           {user.firstName || "Account"}
//                         </span>
//                       </button>

//                       {/* Dropdown menu - FIXED positioning */}
//                       <div
//                         className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                         style={{
//                           backgroundColor:
//                             customStyles.backgroundColor || "#1F2937",
//                           borderRadius:
//                             //  customStyles.borderRadius ||
//                             "0.375rem",
//                         }}
//                       >
//                         {user.isAdmin && (
//                           <Link
//                             to="/pitech"
//                             className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                             style={{
//                               color: customStyles.textColor || "#D1D5DB",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 customStyles.hoverBackgroundColor || "#374151";
//                               e.currentTarget.style.color =
//                                 customStyles.hoverTextColor || "#93C5FD";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 "transparent";
//                               e.currentTarget.style.color =
//                                 customStyles.textColor || "#D1D5DB";
//                             }}
//                           >
//                             Admin Dashboard
//                           </Link>
//                         )}
//                         <Link
//                           to="/profile"
//                           onClick={showPageLoading}
//                           className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           style={{ color: customStyles.textColor || "#D1D5DB" }}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               customStyles.hoverBackgroundColor || "#374151";
//                             e.currentTarget.style.color =
//                               customStyles.hoverTextColor || "#93C5FD";
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               "transparent";
//                             e.currentTarget.style.color =
//                               customStyles.textColor || "#D1D5DB";
//                           }}
//                         >
//                           My Profile
//                         </Link>
//                         {!user.isAdmin && (
//                           <Link
//                             to="/orders"
//                             className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                             style={{
//                               color: customStyles.textColor || "#D1D5DB",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 customStyles.hoverBackgroundColor || "#374151";
//                               e.currentTarget.style.color =
//                                 customStyles.hoverTextColor || "#93C5FD";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor =
//                                 "transparent";
//                               e.currentTarget.style.color =
//                                 customStyles.textColor || "#D1D5DB";
//                             }}
//                           >
//                             Orders
//                           </Link>
//                         )}
//                         <button
//                           onClick={handleLogout}
//                           style={{ color: customStyles.textColor || "#D1D5DB" }}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               customStyles.hoverBackgroundColor || "#374151";
//                             e.currentTarget.style.color =
//                               customStyles.hoverTextColor || "#93C5FD";
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor =
//                               "transparent";
//                             e.currentTarget.style.color =
//                               customStyles.textColor || "#D1D5DB";
//                           }}
//                           className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         >
//                           <span className="flex items-center">
//                             <LogOut className="w-4 h-4 mr-1" /> Logout
//                           </span>
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex items-center space-x-2">
//                       <Link
//                         to="/login"
//                         style={
//                           hoveredItem === "login"
//                             ? generateItemStyles(
//                                 null,
//                                 customStyles,
//                                 false,
//                                 true
//                               )
//                             : generateItemStyles(null, customStyles)
//                         }
//                         onMouseEnter={() => setHoveredItem("login")}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200"
//                       >
//                         Login
//                       </Link>
//                       {/* <Link to="/register"
//                         style={hoveredItem === 'logout' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('logout')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
//                         Register
//                     </Link> */}
//                     </div>
//                   )}
//                 </div>

//                 {/* Cart */}
//                 <Link
//                   to="/cart"
//                   className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//                   onClick={(e) => {
//                     e.preventDefault();
//                     window.location.href = "/cart";
//                   }}
//                 >
//                   <ShoppingCartIcon
//                     style={{ color: customStyles.activeBackgroundColor }}
//                     className="w-6 h-6 text-gray-300 group-hover:animate-pulse"
//                   />
//                   {cart.length > 0 && (
//                     <span
//                       className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                       style={{
//                         backgroundColor:
//                           customStyles.activeBackgroundColor || "#2563EB",
//                       }}
//                     >
//                       {cart.length}
//                     </span>
//                   )}
//                 </Link>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Mobile menu - hidden on admin dashboard */}
//       {!isAdminDashboard && (
//         <div
//           className={`md:hidden bg-gray-800 shadow-lg overflow-hidden transition-all duration-300 ${
//             isOpen ? "max-h-98 opacity-100" : "max-h-0 opacity-0"
//           }`}
//           style={{
//             backgroundColor: darkenColor(
//               customStyles.backgroundColor || "#1F2937"
//             ),
//             boxShadow: isOpen
//               ? "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)"
//               : "none",
//           }}
//         >
//           <div className="px-4 pt-2 pb-3 space-y-1">
//             {/* Dynamic mobile menu items */}
//             {!menuLoading && renderMobileMenuItems(menuItems)}

//             {/* <Link
//             onClick={handleLinkClick}
//             to="/blog"
//             style={{color: customStyles.textColor || '#D1D5DB'}}
//             onMouseEnter={(e) => {
//               e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//               e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//             }}
//             onMouseLeave={(e) => {
//               e.currentTarget.style.backgroundColor = 'transparent';
//               e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//             }}
//             className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'}`}
//           >
//             <span className="flex items-center"><Blog className="w-4 h-4 mr-2" /> Blog</span>
//           </Link> */}

//             {/* User options */}
//             {user ? (
//               <>
//                 <Link
//                   onClick={handleLinkClick}
//                   to="/profile"
//                   style={{
//                     color: customStyles.textColor || "#D1D5DB",
//                     transform: "none",
//                     boxShadow: "none",
//                     border: "none",
//                   }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       customStyles.hoverBackgroundColor || "#374151";
//                     e.currentTarget.style.color =
//                       customStyles.hoverTextColor || "#93C5FD";
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.backgroundColor = "transparent";
//                     e.currentTarget.style.color =
//                       customStyles.textColor || "#D1D5DB";
//                   }}
//                   className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//                 >
//                   <span className="flex items-center">
//                     <User className="w-4 h-4 mr-2" /> My Profile
//                   </span>
//                 </Link>
//                 {!user.isAdmin && (
//                   <Link
//                     onClick={handleLinkClick}
//                     to="/orders"
//                     style={{ color: customStyles.textColor || "#D1D5DB" }}
//                     onMouseEnter={(e) => {
//                       e.currentTarget.style.backgroundColor =
//                         customStyles.hoverBackgroundColor || "#374151";
//                       e.currentTarget.style.color =
//                         customStyles.hoverTextColor || "#93C5FD";
//                     }}
//                     onMouseLeave={(e) => {
//                       e.currentTarget.style.backgroundColor = "transparent";
//                       e.currentTarget.style.color =
//                         customStyles.textColor || "#D1D5DB";
//                     }}
//                     className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//                   >
//                     <span className="flex items-center">
//                       <ShoppingBag className="w-4 h-4 mr-2" /> Orders
//                     </span>
//                   </Link>
//                 )}
//                 <button
//                   onClick={() => {
//                     handleLinkClick();
//                     handleLogout();
//                   }}
//                   style={{ color: customStyles.textColor || "#D1D5DB" }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       customStyles.hoverBackgroundColor || "#374151";
//                     e.currentTarget.style.color =
//                       customStyles.hoverTextColor || "#93C5FD";
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.backgroundColor = "transparent";
//                     e.currentTarget.style.color =
//                       customStyles.textColor || "#D1D5DB";
//                   }}
//                   className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//                 >
//                   <span className="flex items-center">
//                     <LogOut className="w-4 h-4 mr-2" /> Logout
//                   </span>
//                 </button>
//               </>
//             ) : (
//               <div className="flex flex-col space-y-1 px-3 py-2">
//                 <Link
//                   onClick={handleLinkClick}
//                   to="/login"
//                   style={{
//                     color: customStyles.textColor || "#D1D5DB",
//                     backgroundColor:
//                       customStyles.activeBackgroundColor || "transparent",
//                   }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       customStyles.hoverBackgroundColor || "#374151";
//                     e.currentTarget.style.color =
//                       customStyles.hoverTextColor || "#93C5FD";
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       customStyles.activeBackgroundColor || "transparent";
//                     e.currentTarget.style.color =
//                       customStyles.textColor || "#D1D5DB";
//                   }}
//                   className="w-full block py-2 text-center rounded-md text-white bg-blue-900 font-semibold hover:font-bold hover:bg-gray-700 hover:text-blue-400"
//                 >
//                   Login
//                 </Link>
//                 {/* <Link
//                 onClick={handleLinkClick}
//                 to="/register"
//                 style={{
//                   color: customStyles.textColor || '#D1D5DB',
//                   backgroundColor : customStyles.activeBackgroundColor ||'transparent'}}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                   e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.activeBackgroundColor || 'transparent';
//                   e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                 }}
//                 className="w-full block py-2 text-center rounded-md text-white bg-blue-600 font-semibold hover:font-bold hover:bg-blue-500"
//               >
//                 Register
//               </Link>           */}
//               </div>
//             )}

//             {/* Admin Dashboard link at the end - highlighted */}
//             {user?.isAdmin && (
//               <>
//                 <Link
//                   onClick={handleLinkClick}
//                   to="/pitech"
//                   style={{
//                     color: customStyles.textColor || "#D1D5DB",
//                     backgroundColor:
//                       customStyles.activeBackgroundColor || "transparent",
//                   }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       customStyles.hoverBackgroundColor || "#374151";
//                     e.currentTarget.style.color =
//                       customStyles.hoverTextColor || "#93C5FD";
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.backgroundColor =
//                       customStyles.activeBackgroundColor || "transparent";
//                     e.currentTarget.style.color =
//                       customStyles.textColor || "#D1D5DB";
//                   }}
//                   className="w-full block py-3 mt-3 text-center rounded-md text-white bg-blue-600 hover:bg-blue-500 font-medium"
//                 >
//                   <span className="flex items-center justify-center">
//                     <UserRoundCog className="w-5 h-5 mr-2" /> Admin Dashboard
//                   </span>
//                 </Link>
//               </>
//             )}
//           </div>
//         </div>
//       )}
//     </nav>
//   );
// };

// export default Navbar;

// // berofe phone and email
// // src/components/Navbar.js
// import React, { useState, useEffect } from 'react';
// import { Link, useLocation } from 'react-router-dom';
// import { useAuth } from '../context/AuthContext';
// import { useCart } from '../context/CartContext';
// import axios from 'axios';
// import {
//   ShoppingCartIcon, Menu, X, User, Package, Home, Users as Info, Mail,
//   LogOut, UserRoundCog, ShoppingBag, LoaderPinwheel as Blog,
//   ExternalLink, FileText, Phone, MessageCircle, Calendar, Map, Bookmark,
//   Award, Database, Layers, Coffee, Gift, Tag, Clock, CreditCard, PenTool,
//   Truck, Image, Heart, Star, Settings, Linkedin, Twitter, Facebook, Instagram,
//   Youtube, ChevronDown, ChevronRight,
// } from 'lucide-react';

// // Map icon names to components
// const iconMap = {
//   Home: Home,
//   Package: Package,
//   Mail: Mail,
//   Users: Info,
//   Info: Info,
//   ExternalLink: ExternalLink,
//   ShoppingCart: ShoppingCartIcon,
//   User: User,
//   Settings: Settings,
//   FileText: FileText,
//   Phone: Phone,
//   MessageCircle: MessageCircle,
//   Calendar: Calendar,
//   Map: Map,
//   Bookmark: Bookmark,
//   Award: Award,
//   Database: Database,
//   Layers: Layers,
//   Coffee: Coffee,
//   Gift: Gift,
//   Tag: Tag,
//   Clock: Clock,
//   CreditCard: CreditCard,
//   PenTool: PenTool,
//   Truck: Truck,
//   Image: Image,
//   Heart: Heart,
//   Star: Star,
//   ShoppingBag: ShoppingBag,
//   Linkedin: Linkedin,
//   Twitter: Twitter,
//   Facebook: Facebook,
//   Instagram: Instagram,
//   Youtube: Youtube
// };

// // Function to render icon based on name
// const renderIcon = (iconName) => {
//   if (!iconName) return null;

//   const IconComponent = iconMap[iconName];
//   if (IconComponent) {
//     return <IconComponent className="w-4 h-4 mr-1" />;
//   }
//   return null;
// };

// // Generate inline styles from menu styling options
// const generateItemStyles = (item, baseStyles, isActive = false, hoverState = false) => {
//   const styles = { ...baseStyles };

//   // Apply menu's base styles
//   if (baseStyles) {
//     if (!hoverState && !isActive) {
//       if (baseStyles.textColor) styles.color = baseStyles.textColor;
//       if (baseStyles.backgroundColor) styles.backgroundColor = baseStyles.backgroundColor;
//     } else if (hoverState && !isActive) {
//       if (baseStyles.hoverTextColor) styles.color = baseStyles.hoverTextColor;
//       if (baseStyles.hoverBackgroundColor) styles.backgroundColor = baseStyles.hoverBackgroundColor;
//     } else if (isActive) {
//       if (baseStyles.activeTextColor) styles.color = baseStyles.activeTextColor;
//       if (baseStyles.activeBackgroundColor) styles.backgroundColor = baseStyles.activeBackgroundColor;
//     }

//     if (baseStyles.fontFamily) styles.fontFamily = baseStyles.fontFamily;
//     if (baseStyles.fontSize) styles.fontSize = baseStyles.fontSize;
//     if (baseStyles.fontWeight) styles.fontWeight = baseStyles.fontWeight;
//     if (baseStyles.borderRadius) styles.borderRadius = baseStyles.borderRadius;
//   }

//   // Override with item-specific styles if provided
//   if (item?.styles) {
//     if (!hoverState && !isActive) {
//       if (item.styles.textColor) styles.color = item.styles.textColor;
//       if (item.styles.backgroundColor) styles.backgroundColor = item.styles.backgroundColor;
//     } else if (hoverState && !isActive) {
//       if (item.styles.hoverTextColor) styles.color = item.styles.hoverTextColor;
//       if (item.styles.hoverBackgroundColor) styles.backgroundColor = item.styles.hoverBackgroundColor;
//     }

//     if (item.styles.fontFamily) styles.fontFamily = item.styles.fontFamily;
//     if (item.styles.fontSize) styles.fontSize = item.styles.fontSize;
//     if (item.styles.fontWeight) styles.fontWeight = item.styles.fontWeight;
//     if (item.styles.borderRadius) styles.borderRadius = item.styles.borderRadius;
//   }

//   return styles;
// };

// const Navbar = ({ menuName = 'navbar', fallbackMenuName = 'main' }) => {
//   const { user, logout } = useAuth();
//   const { cart } = useCart();
//   const [isOpen, setIsOpen] = useState(false);
//   const [isScrolled, setIsScrolled] = useState(false);
//   const location = useLocation();

//   const [expandedMobileItems, setExpandedMobileItems] = useState({});

//   //for centered scrolling
//   const [showLogo, setShowLogo] = useState(true);
//   const [navbarHeight, setNavbarHeight] = useState(null);
//   const [navbarOpacity, setNavbarOpacity] = useState(1);

//   // Dynamic menu state
//   const [menuData, setMenuData] = useState(null);
//   const [menuItems, setMenuItems] = useState([]);
//   const [menuLoading, setMenuLoading] = useState(true);

//   // Hover state for menu items
//   const [hoveredItem, setHoveredItem] = useState(null);

//   // Custom style states
//   const [navbarLogo, setNavbarLogo] = useState(null);
//   const [customStyles, setCustomStyles] = useState({});
//   const [navbarOptions, setNavbarOptions] = useState({});

//   // Check if current path is the admin dashboard
//   const isAdminDashboard = location.pathname === '/pitech';

//   // Effect for handling admin dashboard specific styling
//   useEffect(() => {
//     const fixAdminDashboardPadding = () => {
//       // Fix for admin dashboard
//       if (isAdminDashboard) {
//         document.body.style.paddingTop = '80px'; // Fixed padding for admin dashboard

//         const navbar = document.querySelector('nav');
//         if (navbar) {
//           navbar.style.height = '80px'; // Fixed height for navbar on admin dashboard
//           setNavbarHeight('80px');
//           if (navbarOptions.layout === 'centered') {
//             setShowLogo(true);
//           }
//         }
//       }
//     };
//     fixAdminDashboardPadding();
//   });

//   useEffect(() => {
//     // Set initial navbar positioning immediately
//     const navbar = document.querySelector('nav');
//     if (navbar) {
//       navbar.style.top = '0';
//       navbar.style.position = 'fixed';
//     }
//   }, []);

//   //function to sort menu items by order
//   const sortMenuItemsByOrder = (items) => {
//     return [...items].sort((a, b) => a.order - b.order);
//   };

//   // Fetch dynamic menu items
//   useEffect(() => {
//     const fetchMenu = async () => {
//       try {
//         const token = localStorage.getItem("token");

//         const response = await axios.get(`/api/menus/name/${menuName}`);
//         const menu = response.data;

//         setMenuData(menu);

//         // Create a deep copy of the menu items array and sort by order
//         let sortedItems = [];
//         if (menu.items && menu.items.length > 0) {
//           // Create deep copy to avoid mutation issues
//           sortedItems = JSON.parse(JSON.stringify(menu.items));
//           // Sort by order property
//           sortedItems.sort((a, b) => {
//             // If order properties are the same, maintain original order
//             if (a.order === b.order) return 0;
//             // Otherwise sort by order property
//             return a.order - b.order;
//           });

//           // Also sort children of each item
//           sortedItems.forEach(item => {
//             if (item.children && item.children.length > 0) {
//               item.children.sort((a, b) => a.order - b.order);
//             }
//           });
//         }

//         // Set the sorted items in state
//         setMenuItems(sortedItems);

//         // Extract styling information
//         if (menu.styles) {
//           setCustomStyles(menu.styles);
//         }

//         if (menu.navbarOptions) {
//           setNavbarOptions(menu.navbarOptions);
//           if (menu.navbarOptions.logo) {
//             setNavbarLogo(menu.navbarOptions.logo);
//           }
//         }

//         // Load custom CSS if provided
//         if (menu.customCss) {
//           // Create a style element for custom CSS
//           const styleElement = document.createElement('style');
//           styleElement.id = `menu-custom-css-${menu._id}`;
//           styleElement.textContent = menu.customCss;
//           document.head.appendChild(styleElement);

//           // Cleanup function
//           return () => {
//             const existingStyle = document.getElementById(`menu-custom-css-${menu._id}`);
//             if (existingStyle) {
//               document.head.removeChild(existingStyle);
//             }
//           };
//         }
//       } catch (error) {
//         console.error('Error fetching menu:', error);
//       } finally {
//         setMenuLoading(false);
//       }
//     };

//     fetchMenu();
//   }, [menuName]);

//   const toggleMobileItem = (itemId) => {
//     setExpandedMobileItems(prev => ({
//       ...prev,
//       [itemId]: !prev[itemId]
//     }));
//   };

//   // Generate CSS classes based on configuration
//   const getNavbarClasses = () => {
//     const baseClasses = 'fixed w-full z-50 transition-all duration-300';

//     // Add background color classes based on scroll state
//     const bgClasses = isScrolled
//       ? 'bg-gray-900 shadow-lg py-2'
//       : 'bg-gray-800/90 backdrop-blur-sm py-4';

//     return `${baseClasses} ${bgClasses} ${customStyles.customClasses || ''}`;
//   };

//   // Generate container class based on navbarOptions
//   const getContainerClasses = () => {
//     let containerClass = 'w-full px-4 sm:px-6 lg:px-8';

//     if (navbarOptions.containerMaxWidth && navbarOptions.containerMaxWidth !== '100%') {
//       containerClass += ' mx-auto';
//     }

//     return containerClass;
//   };

//   function darkenColor(hex, amount = 20) {
//     let col = hex.startsWith('#') ? hex.slice(1) : hex;
//     if (col.length === 3) col = col.split('').map(c => c + c).join('');

//     const num = parseInt(col, 16);
//     const r = Math.max(0, (num >> 16) - amount);
//     const g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
//     const b = Math.max(0, (num & 0x0000FF) - amount);

//     return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
//   }

//   // Render menu items dynamically
//   const renderMenuItems = (items) => {
//     return items.map(item => {
//       // Skip items that should not be shown on this device
//       if ((item.mobileOnly && window.innerWidth >= parseInt(navbarOptions.mobileBreakpoint || '768px')) ||
//           (item.desktopOnly && window.innerWidth < parseInt(navbarOptions.mobileBreakpoint || '768px'))) {
//         return null;
//       }

//       const isActive = location.pathname === item.path;
//       const itemClasses = `px-2 py-2 rounded-md text-sm font-medium ${
//         isActive ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'
//       } transition-all duration-200 ${item.customClasses || ''}`;

//       // Generate dynamic styles
//       const itemStyles = generateItemStyles(item, customStyles, isActive);
//       const hoverStyles = generateItemStyles(item, customStyles, isActive, true);

//       return (
//         <React.Fragment key={item._id}>
//           {/* Render child items only if they exist */}
//           {item.children && item.children.length > 0 ? (
//             <div className="group relative inline-block hover:delay-150">
//               {/* Parent menu item */}
//               <div
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${
//                   location.pathname === item.path ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'
//                 } transition-all duration-200 cursor-pointer flex items-center ${item.customClasses || ''}`}
//                 style={hoveredItem === item._id ? hoverStyles : itemStyles}
//                 onMouseEnter={() => setHoveredItem(item._id)}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 {item.icon && renderIcon(item.icon)}
//                 {item.title}
//                 <ChevronDown className="w-3 h-3 ml-1" />
//               </div>

//               {/* Dropdown for children */}
//               <div className="absolute left-0 -mt-2 pt-6 hidden group-hover:block bg-gray-800 rounded-md shadow-lg py-1 min-w-max z-50 transition-opacity duration-300 group-hover:opacity-100 opacity-0 group-hover:delay-0">
//                 {item.children.map(child => {
//                   // Skip children based on display settings
//                   if ((child.mobileOnly && window.innerWidth >= parseInt(navbarOptions.mobileBreakpoint || '768px')) ||
//                       (child.desktopOnly && window.innerWidth < parseInt(navbarOptions.mobileBreakpoint || '768px'))) {
//                     return null;
//                   }

//                   const isChildActive = location.pathname === child.path;
//                   const childStyles = generateItemStyles(child, customStyles, isChildActive);
//                   const childHoverStyles = generateItemStyles(child, customStyles, isChildActive, true);

//                   return (
//                     <React.Fragment key={child._id}>
//                       {child.isExternal ? (
//                         <a
//                           href={child.path}
//                           target="_blank"
//                           rel="noopener noreferrer"
//                           className={`block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400 whitespace-nowrap ${child.customClasses || ''}`}
//                           style={hoveredItem === child._id ? childHoverStyles : childStyles}
//                           onMouseEnter={() => setHoveredItem(child._id)}
//                           onMouseLeave={() => setHoveredItem(null)}
//                         >
//                           <span className="flex items-center">
//                             {child.icon && renderIcon(child.icon)}
//                             {child.title}
//                             {!child.icon && <ExternalLink className="w-3 h-3 ml-1" />}
//                           </span>
//                         </a>
//                       ) : (
//                         <Link
//                           to={child.path}
//                           onClick={showPageLoading}
//                           className={`block px-4 py-2 text-sm ${
//                             location.pathname === child.path ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:bg-gray-700 hover:text-blue-400'
//                           } whitespace-nowrap ${child.customClasses || ''}`}
//                           style={hoveredItem === child._id ? childHoverStyles : childStyles}
//                           onMouseEnter={() => setHoveredItem(child._id)}
//                           onMouseLeave={() => setHoveredItem(null)}
//                         >
//                           <span className="flex items-center">
//                             {child.icon && renderIcon(child.icon)}
//                             {child.title}
//                           </span>
//                         </Link>
//                       )}
//                     </React.Fragment>
//                   );
//                 })}
//               </div>
//             </div>
//           ) : (
//             // Regular menu item with no children
//             item.isExternal ? (
//               <a
//                 href={item.path}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className={itemClasses}
//                 style={hoveredItem === item._id ? hoverStyles : itemStyles}
//                 onMouseEnter={() => setHoveredItem(item._id)}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center">
//                   {item.icon && renderIcon(item.icon)}
//                   {item.title}
//                   {!item.icon && <ExternalLink className="w-3 h-3 ml-1" />}
//                 </span>
//               </a>
//             ) : (
//               <Link
//                 to={item.path}
//                 onClick={showPageLoading}
//                 className={itemClasses}
//                 style={hoveredItem === item._id ? hoverStyles : itemStyles}
//                 onMouseEnter={() => setHoveredItem(item._id)}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center">
//                   {item.icon && renderIcon(item.icon)}
//                   {item.title}
//                 </span>
//               </Link>
//             )
//           )}
//         </React.Fragment>
//       );
//     });
//   };

//   // Function to render mobile menu items recursively
//   const renderMobileMenuItems = (items, depth = 0) => {
//     return items.map(item => {
//       // Skip items that should not be shown on mobile
//       if (item.desktopOnly) {
//         return null;
//       }

//       const isActive = location.pathname === item.path;
//       const itemStyles = generateItemStyles(item, customStyles, isActive);

//       return (
//         <React.Fragment key={item._id}>
//           {item.children && item.children.length > 0 ? (
//             <div>
//               <div
//                 className={`block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700 ${depth > 0 ? 'pl-8' : ''} cursor-pointer ${item.customClasses || ''}`}
//                 onClick={() => toggleMobileItem(item._id)}
//                 style={itemStyles}
//               >
//                 <span className="flex items-center justify-between">
//                   <span className="flex items-center">
//                     {item.icon && renderIcon(item.icon)}
//                     {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
//                     {item.title}
//                   </span>
//                   <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expandedMobileItems[item._id] ? 'rotate-180' : ''}`} />
//                 </span>
//               </div>

//               <div className={`ml-2 border-l border-gray-700 overflow-hidden transition-all duration-300 ${expandedMobileItems[item._id] ? 'max-h-96' : 'max-h-0'}`}>
//                 {renderMobileMenuItems(item.children.filter(child => !child.desktopOnly), depth + 1)}
//               </div>
//             </div>
//           ) : (
//             item.isExternal ? (
//               <a
//                 href={item.path}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className={`block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700 ${depth > 0 ? 'pl-8' : ''} ${item.customClasses || ''}`}
//                 onClick={handleLinkClick}
//                 style={itemStyles}
//               >
//                 <span className="flex items-center">
//                   {item.icon && renderIcon(item.icon)}
//                   {!item.icon && depth === 0 && <ExternalLink className="w-4 h-4 mr-2" />}
//                   {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
//                   {item.title}
//                 </span>
//               </a>
//             ) : (
//               <Link
//                 to={item.path}
//                 onClick={handleLinkClick}
//                 className={`block px-3 py-2 rounded-md text-base font-medium ${
//                   location.pathname === item.path ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'
//                 } ${depth > 0 ? 'pl-8' : ''} ${item.customClasses || ''}`}
//                 style={itemStyles}
//               >
//                 <span className="flex items-center">
//                   {item.icon && renderIcon(item.icon)}
//                   {depth > 0 && <ChevronRight className="w-3 h-3 mr-1" />}
//                   {item.title}
//                 </span>
//               </Link>
//             )
//           )}
//         </React.Fragment>
//       );
//     });
//   };

//   // scroll effect
//   useEffect(() => {
//     const handleScroll = () => {
//       if (window.scrollY > 10) {
//         setIsScrolled(true);
//         setNavbarOpacity(0.9); // Set opacity when scrolled
//         // In centered layout when scrolled
//         if (navbarOptions.layout === 'centered') {
//           setShowLogo(false);
//           setNavbarHeight('80px'); // Set fixed height when scrolled
//         }
//       } else {
//         setIsScrolled(false);
//         setNavbarOpacity(1); // Reset to full opacity when at top
//         setShowLogo(true);
//         // Reset to original height from options
//         setNavbarHeight(window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || '768px')
//           ? navbarOptions.heightDesktop || '80px'
//           : navbarOptions.heightMobile || '70px');
//       }
//     };

//     // Initialize the height and opacity
//     setNavbarHeight(window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || '768px')
//       ? navbarOptions.heightDesktop || '80px'
//       : navbarOptions.heightMobile || '70px');

//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, [navbarOptions.layout, navbarOptions.heightDesktop, navbarOptions.heightMobile, navbarOptions.mobileBreakpoint]);

//   useEffect(() => {
//     // Get navbar height from options or use defaults
//     const desktopHeight = navbarOptions.heightDesktop || '80px';
//     const mobileHeight = navbarOptions.heightMobile || '70px';
//     const mobileBreakpoint = navbarOptions.mobileBreakpoint || '768px';

//     // Convert px values to numbers for calculations
//     const getPixelValue = (value) => {
//       if (typeof value === 'string') {
//         return parseInt(value) || (value.includes('px') ? parseInt(value) : 80);
//       }
//       return value || 80;
//     };

//     // Set the correct height directly on the navbar element
//     const navbar = document.querySelector('nav');
//     if (navbar) {
//       // Remove negative margins and set position instead
//       navbar.style.marginTop = '0';
//       navbar.style.top = '0';

//       if (window.innerWidth > parseInt(mobileBreakpoint)) {
//         navbar.style.height = `${getPixelValue(desktopHeight)}px`;
//       } else {
//         navbar.style.height = `${getPixelValue(mobileHeight)}px`;
//       }
//     }

//     // Add padding to body to match the navbar height
//     document.body.style.paddingTop = window.innerWidth > parseInt(mobileBreakpoint)
//       ? `${getPixelValue(desktopHeight)}px`
//       : `${getPixelValue(mobileHeight)}px`;

//     // Add media query for responsive padding
//     const mediaQuery = window.matchMedia(`(max-width: ${mobileBreakpoint})`);
//     const handleMediaChange = (e) => {
//       if (e.matches) {
//         document.body.style.paddingTop = `${getPixelValue(mobileHeight)}px`;
//         if (navbar) navbar.style.height = `${getPixelValue(mobileHeight)}px`;
//       } else {
//         document.body.style.paddingTop = `${getPixelValue(desktopHeight)}px`;
//         if (navbar) navbar.style.height = `${getPixelValue(desktopHeight)}px`;
//       }
//     };

//     // Set initial padding based on screen size
//     handleMediaChange(mediaQuery);

//     // Add listener for screen size changes
//     mediaQuery.addEventListener('change', handleMediaChange);

//     // Clean up
//     return () => {
//       mediaQuery.removeEventListener('change', handleMediaChange);
//       document.body.style.paddingTop = '0';
//     };
//   }, [navbarOptions]);

//   const toggleMenu = () => setIsOpen(!isOpen);

//   // Function to show loading screen
//   const showPageLoading = () => {
//     document.body.classList.add('page-loading');

//     // Automatically remove loading after a short timeout (for demo)
//     setTimeout(() => {
//       document.body.classList.remove('page-loading');
//     }, 600);
//   };

//   // Function to close menu when a mobile link is clicked
//   const handleLinkClick = () => {
//     setIsOpen(false);
//   };

//   const handleLogout = () => {
//     showPageLoading();
//     logout();
//     // Redirect to home or login page after logout
//   };

//   // Get navbar layout style
//   const getNavbarLayout = () => {
//     const layout = navbarOptions.layout || 'standard';

//     switch (layout) {
//       case 'centered':
//         return 'flex-col items-center';
//       case 'split':
//         return 'justify-between items-center';
//       default: // standard
//         return 'justify-between items-center';
//     }
//   };

//   if (menuLoading) {
//     return <div className="fixed w-full h-16 z-50" style={{
//       backgroundColor: 'transparent',
//       transition: 'background-color 0.3s ease'
//     }}></div>;
//   }

//  return (
//   <nav
//     className={getNavbarClasses()}
//     style={{
//       top: '0',
//       position: 'fixed',
//       height: isAdminDashboard
//       ? '80px'
//       : (navbarHeight || (window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || '768px')
//           ? navbarOptions.heightDesktop || '80px'
//           : navbarOptions.heightMobile || '70px')),

//       // height: navbarHeight || (window.innerWidth > parseInt(navbarOptions.mobileBreakpoint || '768px')
//       //   ? navbarOptions.heightDesktop || '80px'
//       //   : navbarOptions.heightMobile || '70px'),
//       // opacity: navbarOpacity,
//       opacity: isAdminDashboard ? 1 : navbarOpacity,
//       transition: 'height 0.3s ease, opacity 0.3s ease',
//       ...(customStyles.backgroundColor && { backgroundColor: customStyles.backgroundColor }),
//       ...(navbarOptions.containerMaxWidth && { maxWidth: navbarOptions.containerMaxWidth })
//     }}

//   >
//     <div className={getContainerClasses()} style={{
//       height: '100%', // Make container take full height
//       ...(navbarOptions.containerMaxWidth ? { maxWidth: navbarOptions.containerMaxWidth } : {})
//     }}>
//       {/* Mobile layout with hamburger-logo-cart */}
//       <div className="flex items-center justify-between w-full md:hidden h-full">
//         <div className="flex-1 flex justify-start">
//           {!isAdminDashboard && (
//             <button
//               onClick={toggleMenu}
//               className="p-1 rounded-md focus:outline-none hover:bg-gray-700 transition-all duration-200"
//               style={{
//                 boxShadow: 'none',
//                 border: 'none',
//                 background: 'transparent',
//                 transform: 'none',
//                 padding: 0,
//                 overflow: 'visible'
//               }}
//             >
//               {isOpen ? (
//                 <X className="w-6 h-6 text-gray-300"
//                 style={{color: customStyles.activeBackgroundColor}} />
//               ) : (
//                 <Menu className="w-6 h-6 text-gray-300"
//                 style={{color: customStyles.activeBackgroundColor}}/>
//               )}
//             </button>
//           )}
//         </div>

//         {/* Logo - center on mobile */}
//         <div className="flex-1 flex items-center justify-center">
//           <Link to="/" className="flex items-center h-full" onClick={showPageLoading}>
//             {navbarLogo ? (
//               <img
//                 className="transition-all duration-300 hover:scale-105"
//                 src={navbarLogo}
//                 alt="Logo"
//                 style={{
//                   maxHeight: navbarOptions.logoHeight || '60px',
//                   width: navbarOptions.logoWidth || 'auto'
//                 }}
//               />
//             ) : (
//               // Fallback logo or text if no logo is set
//               <div className="text-xl font-bold text-white">LOGO</div>
//             )}
//           </Link>
//         </div>

//         {/* Cart - right on mobile */}
//         <div className="flex-1 flex justify-end">
//           <Link to="/cart"
//             className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//             onClick={(e) => {
//               e.preventDefault();
//               window.location.href = '/cart';
//             }}>
//             <ShoppingCartIcon
//              style={{color: customStyles.activeBackgroundColor}}
//             className="w-6 h-6 text-gray-300 group-hover:animate-pulse" />
//             {cart.length > 0 && (
//               <span
//                 className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                 style={{
//                   backgroundColor: customStyles.activeBackgroundColor || '#2563EB'
//                 }}>
//                 {cart.length}
//               </span>
//             )}
//           </Link>
//         </div>
//       </div>

//       {/* Desktop Layout - based on navbarOptions.layout */}
//       <div className="hidden md:flex h-full w-full items-center">
//         {navbarOptions.layout === 'centered' ? (
//           /* Centered Layout (Logo top, menu center) */
//           <div className="flex flex-col w-full h-full justify-center items-center">
//             {/* Logo on top - only shown when not scrolled */}
//             {showLogo && (
//               <div className="flex justify-center mb-2 transition-all duration-300"
//               style={{
//                 display: (isAdminDashboard) ? 'none' : 'block'
//               }}>
//                 <Link to="/" className="flex items-center" onClick={showPageLoading}>
//                   {navbarLogo ? (
//                     <img
//                       className="transition-all duration-300 hover:scale-105"
//                       src={navbarLogo}
//                       alt="Logo"
//                       style={{
//                         maxHeight: navbarOptions.logoHeight || '60px',
//                         width: navbarOptions.logoWidth || 'auto'
//                       }}
//                     />
//                   ) : (
//                     <div className="text-xl font-bold text-white">LOGO</div>
//                   )}
//                 </Link>
//               </div>
//             )}

//             {/* Menu items centered */}
//             <div className="flex justify-center items-center space-x-2">
//             {/* <div className={`flex justify-center items-center space-x-2 transition-all duration-300 ${!showLogo ? 'py-2' : ''}`}> */}
//               {!menuLoading && renderMenuItems(menuItems)}

//               {/* <Link to="/blog"
//                 onClick={showPageLoading}
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
//                 style={location.pathname === '/blog' ?
//                   generateItemStyles(null, customStyles, true) :
//                   generateItemStyles(null, customStyles)}
//                 onMouseEnter={() => setHoveredItem('blog')}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
//               </Link> */}

//               {/* User and cart buttons */}
//               <div className="flex items-center space-x-2">
//                 <div className="relative">
//                   {user ? (
//                     <div className="flex items-center group">
//                       <button
//                         className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
//                         style={{ transform: 'none', boxShadow: 'none', border: 'none' ,
//                           color: customStyles.textColor || '#D1D5DB'}}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                             e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor = 'transparent';
//                             e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}>
//                         <User className="w-5 h-5" />
//                         <span className="text-sm font-medium">{user.firstName || 'Account'}</span>
//                       </button>

//                       {/* Dropdown menu */}
//                       <div className="absolute left-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                         style={{
//                           backgroundColor: customStyles.backgroundColor || '#1F2937',
//                           borderRadius:
//                           // customStyles.borderRadius ||
//                            '0.375rem'
//                         }}
//                       >
//                         {user.isAdmin && (
//                           <Link to="/pitech" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                             style={{color: customStyles.textColor || '#D1D5DB'}}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                               e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.backgroundColor = 'transparent';
//                               e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                             }}>
//                             Admin Dashboard
//                           </Link>
//                         )}
//                         <Link to="/profile" onClick={showPageLoading}
//                           className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           style={{color: customStyles.textColor || '#D1D5DB'}}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                             e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor = 'transparent';
//                             e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                           }}>
//                           My Profile
//                         </Link>
//                         {!user.isAdmin && (
//                           <Link to="/orders" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           style={{color: customStyles.textColor || '#D1D5DB'}}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                             e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor = 'transparent';
//                             e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                           }}>
//                             Orders
//                           </Link>
//                         )}
//                         <button
//                           onClick={handleLogout}
//                           style={{color: customStyles.textColor || '#D1D5DB'}}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                             e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor = 'transparent';
//                             e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                           }}
//                           className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         >
//                           <span className="flex items-center"><LogOut className="w-4 h-4 mr-1" /> Logout</span>
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex items-center space-x-2">
//                       <Link to="/login"
//                        style={hoveredItem === 'login' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('login')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200">
//                         Login
//                       </Link>
//                       <Link to="/register"
//                        style={hoveredItem === 'logout' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('logout')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
//                         Register
//                       </Link>
//                     </div>
//                   )}
//                 </div>

//                 <Link to="/cart"
//                   className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//                   onClick={(e) => {
//                     e.preventDefault();
//                     window.location.href = '/cart';
//                   }}>
//                   <ShoppingCartIcon
//                   style={{color: customStyles.activeBackgroundColor}}
//                   className="w-6 h-6 text-gray-300 group-hover:animate-pulse" />
//                   {cart.length > 0 && (
//                     <span
//                       className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                       style={{
//                         backgroundColor: customStyles.activeBackgroundColor || '#2563EB'
//                       }}>
//                       {cart.length}
//                     </span>
//                   )}
//                 </Link>
//               </div>
//             </div>
//           </div>
//         ) : navbarOptions.layout === 'split' ? (
//           /* Split Layout (Logo center, menu on sides) */
//           <div className="flex justify-between items-center w-full h-full">
//             {/* Left menu items */}
//             <div className="flex items-center space-x-2">
//               {!menuLoading && menuItems.slice(0, Math.ceil((menuItems.length / 2)
//               )).map((item, idx) => (
//                 <React.Fragment key={item._id || idx}>
//                   {renderMenuItems([item])}
//                 </React.Fragment>
//               ))}
//             </div>

//             {/* Center logo */}
//             <div className="flex justify-center">
//               <Link to="/" className="flex items-center" onClick={showPageLoading}>
//                 {navbarLogo ? (
//                   <img
//                     className="transition-all duration-300 hover:scale-105"
//                     src={navbarLogo}
//                     alt="Logo"
//                     style={{
//                       maxHeight: navbarOptions.logoHeight || '60px',
//                       width: navbarOptions.logoWidth || 'auto'
//                     }}
//                   />
//                 ) : (
//                   <div className="text-xl font-bold text-white">LOGO</div>
//                 )}
//               </Link>
//             </div>

//             {/* Right menu items + user/cart */}
//             <div className="flex items-center space-x-2">
//               {!menuLoading && menuItems.slice(Math.ceil(( menuItems.length / 2 )
//               )).map((item, idx) => (
//                 <React.Fragment key={item._id || idx}>
//                   {renderMenuItems([item])}
//                 </React.Fragment>
//               ))}

//               {/* <Link to="/blog"
//                 onClick={showPageLoading}
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
//                 style={location.pathname === '/blog' ?
//                   generateItemStyles(null, customStyles, true) :
//                   generateItemStyles(null, customStyles)}
//                 onMouseEnter={() => setHoveredItem('blog')}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
//               </Link> */}

//               {/* User and Cart */}
//               <div className="flex items-center space-x-2">
//                 {user ? (
//                   <div className="flex items-center group relative">
//                     <button
//                       className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
//                       style={{ transform: 'none', boxShadow: 'none', border: 'none' ,
//                         color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                       }}>
//                       <User className="w-5 h-5" />
//                       <span className="text-sm font-medium">{user.firstName || 'Account'}</span>
//                     </button>

//                     {/* Dropdown menu */}
//                     {/* <div className="absolute right-0 mt-2 top-full w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                       style={{
//                         backgroundColor: customStyles.backgroundColor || '#1F2937',
//                         borderRadius: customStyles.borderRadius || '0.375rem'
//                       }}
//                     > */}
//                     <div className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                       style={{
//                         backgroundColor: customStyles.backgroundColor || '#1F2937',
//                         borderRadius:
//                         // customStyles.borderRadius ||
//                         '0.375rem'
//                       }}
//                     >
//                       {user.isAdmin && (
//                         <Link to="/pitech" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           style={{color: customStyles.textColor || '#D1D5DB'}}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                             e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor = 'transparent';
//                             e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                           }}>
//                           Admin Dashboard
//                         </Link>
//                       )}
//                       <Link to="/profile" onClick={showPageLoading}
//                         className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         style={{color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}>
//                         My Profile
//                       </Link>
//                       {!user.isAdmin && (
//                         <Link to="/orders" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         style={{color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}>
//                           Orders
//                         </Link>
//                       )}
//                       <button
//                         onClick={handleLogout}
//                         style={{color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}
//                         className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                       >
//                         <span className="flex items-center"><LogOut className="w-4 h-4 mr-1" /> Logout</span>
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="flex items-center space-x-1">
//                     <Link to="/login"
//                        style={hoveredItem === 'login' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('login')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200">
//                         Login
//                     </Link>
//                     <Link to="/register"
//                         style={hoveredItem === 'logout' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('logout')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
//                         Register
//                     </Link>
//                   </div>
//                 )}

//                 <Link to="/cart"
//                   className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//                   onClick={(e) => {
//                     e.preventDefault();
//                     window.location.href = '/cart';
//                   }}>
//                   <ShoppingCartIcon
//                    style={{color: customStyles.activeBackgroundColor}}
//                    className="w-6 h-6 text-gray-300 group-hover:animate-pulse" />
//                   {cart.length > 0 && (
//                     <span
//                       className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                       style={{
//                         backgroundColor: customStyles.activeBackgroundColor || '#2563EB'
//                       }}>
//                       {cart.length}
//                     </span>
//                   )}
//                 </Link>
//               </div>
//             </div>
//           </div>
//         ) : (
//           /* Standard Layout (Logo left, menu center, actions right) */
//           <div className="flex justify-between items-center w-full h-full">
//             {/* Logo left */}
//             <div className="flex-shrink-0">
//               <Link to="/" className="flex items-center" onClick={showPageLoading}>
//                 {navbarLogo ? (
//                   <img
//                     className="transition-all duration-300 hover:scale-105"
//                     src={navbarLogo}
//                     alt="Logo"
//                     style={{
//                       maxHeight: navbarOptions.logoHeight || '60px',
//                       width: navbarOptions.logoWidth || 'auto'
//                     }}
//                   />
//                 ) : (
//                   <div className="text-xl font-bold text-white">LOGO</div>
//                 )}
//               </Link>
//             </div>

//             {/* Menu center */}
//             <div className="flex-grow flex justify-center items-center space-x-0">
//               {!menuLoading && renderMenuItems(menuItems)}
//               {/* <Link to="/blog"
//                 onClick={showPageLoading}
//                 className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'} transition-all duration-200`}
//                 style={location.pathname === '/blog' ?
//                   generateItemStyles(null, customStyles, true) :
//                   generateItemStyles(null, customStyles)}
//                 onMouseEnter={() => setHoveredItem('blog')}
//                 onMouseLeave={() => setHoveredItem(null)}
//               >
//                 <span className="flex items-center"><Blog className="w-4 h-4 mr-1" /> Blog</span>
//               </Link> */}
//             </div>

//             {/* Actions right */}
//             <div className="flex items-center space-x-2">
//               <div className="relative">
//                 {user ? (
//                   <div className="flex items-center group">
//                     <button
//                       className="flex items-center space-x-1 text-gray-300 hover:text-blue-400 focus:outline-none"
//                       style={{ transform: 'none', boxShadow: 'none', border: 'none' ,
//                         color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                       }}>
//                       <User className="w-5 h-5" />
//                       <span className="text-sm font-medium">{user.firstName || 'Account'}</span>
//                     </button>

//                     {/* Dropdown menu - FIXED positioning */}
//                     <div className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
//                       style={{
//                         backgroundColor: customStyles.backgroundColor || '#1F2937',
//                         borderRadius:
//                         //  customStyles.borderRadius ||
//                          '0.375rem'
//                       }}
//                     >
//                       {user.isAdmin && (
//                         <Link to="/pitech" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                           style={{color: customStyles.textColor || '#D1D5DB'}}
//                           onMouseEnter={(e) => {
//                             e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                             e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                           }}
//                           onMouseLeave={(e) => {
//                             e.currentTarget.style.backgroundColor = 'transparent';
//                             e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                           }}>
//                           Admin Dashboard
//                         </Link>
//                       )}
//                       <Link to="/profile" onClick={showPageLoading}
//                         className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         style={{color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}>
//                         My Profile
//                       </Link>
//                       {!user.isAdmin && (
//                         <Link to="/orders" className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                         style={{color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}>
//                           Orders
//                         </Link>
//                       )}
//                       <button
//                         onClick={handleLogout}
//                         style={{color: customStyles.textColor || '#D1D5DB'}}
//                         onMouseEnter={(e) => {
//                           e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                           e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.currentTarget.style.backgroundColor = 'transparent';
//                           e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                         }}
//                         className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400"
//                       >
//                         <span className="flex items-center"><LogOut className="w-4 h-4 mr-1" /> Logout</span>
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="flex items-center space-x-2">
//                     <Link to="/login"
//                        style={hoveredItem === 'login' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('login')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-gray-300 hover:text-blue-400 border border-transparent hover:border-gray-700 rounded-md transition-all duration-200">
//                         Login
//                     </Link>
//                     <Link to="/register"
//                         style={hoveredItem === 'logout' ?
//                         generateItemStyles(null, customStyles, false, true) :
//                         generateItemStyles(null, customStyles)}
//                         onMouseEnter={() => setHoveredItem('logout')}
//                         onMouseLeave={() => setHoveredItem(null)}
//                         className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-500 border border-transparent  hover:border-gray-700 rounded-md transition-all duration-200">
//                         Register
//                     </Link>
//                   </div>
//                 )}
//               </div>

//               {/* Cart */}
//               <Link to="/cart"
//                 className="relative group p-1 rounded-full hover:animate-pulse transition-all duration-200"
//                 onClick={(e) => {
//                   e.preventDefault();
//                   window.location.href = '/cart';
//                 }}>
//                 <ShoppingCartIcon
//                  style={{color: customStyles.activeBackgroundColor}}
//                 className="w-6 h-6 text-gray-300 group-hover:animate-pulse" />
//                 {cart.length > 0 && (
//                   <span
//                     className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
//                     style={{
//                       backgroundColor: customStyles.activeBackgroundColor || '#2563EB'
//                     }}>
//                     {cart.length}
//                   </span>
//                 )}
//               </Link>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>

//     {/* Mobile menu - hidden on admin dashboard */}
//     {!isAdminDashboard && (
//       <div
//         className={`md:hidden bg-gray-800 shadow-lg overflow-hidden transition-all duration-300 ${
//           isOpen ? 'max-h-98 opacity-100' : 'max-h-0 opacity-0'
//         }`}
//         style={{
//           backgroundColor: darkenColor(customStyles.backgroundColor || '#1F2937'),
//           boxShadow: isOpen ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)' : 'none'
//         }}
//       >
//         <div className="px-4 pt-2 pb-3 space-y-1">
//           {/* Dynamic mobile menu items */}
//           {!menuLoading && renderMobileMenuItems(menuItems)}

//           {/* <Link
//             onClick={handleLinkClick}
//             to="/blog"
//             style={{color: customStyles.textColor || '#D1D5DB'}}
//             onMouseEnter={(e) => {
//               e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//               e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//             }}
//             onMouseLeave={(e) => {
//               e.currentTarget.style.backgroundColor = 'transparent';
//               e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//             }}
//             className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/blog' ? 'text-blue-400 bg-gray-700' : 'text-gray-300 hover:text-blue-400 hover:bg-gray-700'}`}
//           >
//             <span className="flex items-center"><Blog className="w-4 h-4 mr-2" /> Blog</span>
//           </Link> */}

//           {/* User options */}
//           {user ? (
//             <>
//               <Link
//                 onClick={handleLinkClick}
//                 to="/profile"
//                 style={{color: customStyles.textColor || '#D1D5DB',
//                   transform: 'none', boxShadow: 'none', border: 'none'
//                 }}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                   e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = 'transparent';
//                   e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                 }}
//                 className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//               >
//                 <span className="flex items-center"><User className="w-4 h-4 mr-2" /> My Profile</span>
//               </Link>
//               {!user.isAdmin && (
//                 <Link
//                   onClick={handleLinkClick}
//                   to="/orders"
//                   style={{color: customStyles.textColor || '#D1D5DB'}}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                     e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.backgroundColor = 'transparent';
//                     e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                   }}
//                   className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//                 >
//                   <span className="flex items-center"><ShoppingBag className="w-4 h-4 mr-2" /> Orders</span>
//                 </Link>
//               )}
//               <button
//                 onClick={() => {
//                   handleLinkClick();
//                   handleLogout();
//                 }}
//                 style={{color: customStyles.textColor || '#D1D5DB'}}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                   e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = 'transparent';
//                   e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                 }}
//                 className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-blue-400 hover:bg-gray-700"
//               >
//                 <span className="flex items-center"><LogOut className="w-4 h-4 mr-2" /> Logout</span>
//               </button>
//             </>
//           ) : (
//             <div className="flex flex-col space-y-1 px-3 py-2">
//               <Link
//                 onClick={handleLinkClick}
//                 to="/login"
//                 style={{
//                   color: customStyles.textColor || '#D1D5DB',
//                   backgroundColor : customStyles.activeBackgroundColor ||'transparent'}}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                   e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.activeBackgroundColor || 'transparent';
//                   e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                 }}
//                 className="w-full block py-2 text-center rounded-md text-white bg-blue-900 font-semibold hover:font-bold hover:bg-gray-700 hover:text-blue-400"
//               >
//                 Login
//               </Link>
//               <Link
//                 onClick={handleLinkClick}
//                 to="/register"
//                 style={{
//                   color: customStyles.textColor || '#D1D5DB',
//                   backgroundColor : customStyles.activeBackgroundColor ||'transparent'}}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                   e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.activeBackgroundColor || 'transparent';
//                   e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                 }}
//                 className="w-full block py-2 text-center rounded-md text-white bg-blue-600 font-semibold hover:font-bold hover:bg-blue-500"
//               >
//                 Register
//               </Link>
//             </div>
//           )}

//           {/* Admin Dashboard link at the end - highlighted */}
//           {user?.isAdmin && (
//             <>
//               <Link
//                 onClick={handleLinkClick}
//                 to="/pitech"
//                 style={{
//                   color: customStyles.textColor || '#D1D5DB',
//                   backgroundColor : customStyles.activeBackgroundColor ||'transparent'}}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.hoverBackgroundColor || '#374151';
//                   e.currentTarget.style.color = customStyles.hoverTextColor || '#93C5FD';
//                 }}
//                 onMouseLeave={(e) => {
//                   e.currentTarget.style.backgroundColor = customStyles.activeBackgroundColor || 'transparent';
//                   e.currentTarget.style.color = customStyles.textColor || '#D1D5DB';
//                 }}
//                 className="w-full block py-3 mt-3 text-center rounded-md text-white bg-blue-600 hover:bg-blue-500 font-medium"
//               >
//                 <span className="flex items-center justify-center"><UserRoundCog className="w-5 h-5 mr-2" /> Admin Dashboard</span>
//               </Link>
//             </>
//           )}
//         </div>
//       </div>
//     )}
//   </nav>
// );
// };

// export default Navbar;
